import { createClient, SupabaseClient, User } from '@supabase/supabase-js';
import {
  Profile,
  Job,
  Quote,
  Message,
  Notification,
  Review,
  ServiceCategory,
  VerificationStatus,
  UserRole,
  ProfessionalSubscription,
  SubscriptionPayment,
  SubscriptionStatus,
  AdminAIHealthStats,
  MarketplaceTransaction,
  ProfessionalEarningsSummary,
  ProfessionalPayout,
  ProfessionalBankAccount,
  NigerianBankOption,
  AdminPlatformRevenue,
  AIJobAnalysis,
  AIEstimateResult,
  EligibleChatContact,
} from '../types';
import { INITIAL_PROFILES, INITIAL_JOBS, INITIAL_QUOTES, INITIAL_NOTIFICATIONS, INITIAL_MESSAGES, INITIAL_REVIEWS } from '../data/initialData';
import { SERVICE_CATEGORIES } from '../data/categories';

function cleanEnvValue(val?: string): string {
  if (!val || typeof val !== 'string') return '';
  let trimmed = val.trim();
  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    trimmed = trimmed.slice(1, -1).trim();
  }
  if (trimmed === 'undefined' || trimmed === 'null') return '';
  return trimmed;
}

function normalizeSupabaseUrl(rawUrl?: string): string {
  const cleaned = cleanEnvValue(rawUrl);
  if (!cleaned) return '';
  let candidate = cleaned;
  if (!/^https?:\/\//i.test(candidate)) {
    candidate = `https://${candidate}`;
  }
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return candidate;
    }
  } catch {
    return '';
  }
  return '';
}

function normalizeSupabaseKey(rawKey?: string): string {
  const cleaned = cleanEnvValue(rawKey);
  if (!cleaned) return '';
  return cleaned;
}

const getClientEnv = (key: string): string | undefined => {
  if (typeof window !== 'undefined' && (window as any).__JOBMATCH_ENV__ && (window as any).__JOBMATCH_ENV__[key]) {
    return (window as any).__JOBMATCH_ENV__[key] as string;
  }
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env[key]) {
      return (import.meta as any).env[key] as string;
    }
  } catch {}
  try {
    if (typeof process !== 'undefined' && process.env && process.env[key]) {
      return process.env[key] as string;
    }
  } catch {}
  return undefined;
};

const rawSupabaseUrl =
  getClientEnv('VITE_SUPABASE_URL') ||
  getClientEnv('SUPABASE_URL') ||
  getClientEnv('VITE_PUBLIC_SUPABASE_URL') ||
  getClientEnv('NEXT_PUBLIC_SUPABASE_URL');

const rawSupabaseAnonKey =
  getClientEnv('VITE_SUPABASE_ANON_KEY') ||
  getClientEnv('SUPABASE_ANON_KEY') ||
  getClientEnv('VITE_SUPABASE_PUBLISHABLE_KEY') ||
  getClientEnv('SUPABASE_PUBLISHABLE_KEY') ||
  getClientEnv('VITE_SUPABASE_KEY') ||
  getClientEnv('SUPABASE_KEY') ||
  getClientEnv('NEXT_PUBLIC_SUPABASE_ANON_KEY');

export const supabaseUrl: string = normalizeSupabaseUrl(rawSupabaseUrl);
export const supabaseAnonKey: string = normalizeSupabaseKey(rawSupabaseAnonKey);

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

function maskEmailDiagnostic(email?: string): string {
  if (!email || !email.includes('@')) return '***@***';
  const [local, domain] = email.split('@');
  const visible = local.length > 2 ? `${local.slice(0, 2)}***` : '***';
  return `${visible}@${domain}`;
}

export interface SignUpAuthResult {
  profile: Profile;
  user: any;
  session: any | null;
  requiresEmailConfirmation: boolean;
}

export function getSupabaseAuthErrorMessage(error: any): string {
  if (!error) return 'An unexpected error occurred during authentication.';

  const rawMsg = typeof error === 'string' ? error : (error.message || error.error_description || error.msg || '');
  const msg = rawMsg.toLowerCase();
  const code = ((error && error.code) || '').toLowerCase();
  const status = error && error.status;

  // 1. Error sending confirmation email (Supabase SMTP issues)
  if (
    msg.includes('error sending confirmation email') ||
    msg.includes('error sending verification email') ||
    msg.includes('sending confirmation email')
  ) {
    return 'Supabase was unable to send the confirmation email (Supabase SMTP error). Please verify your SMTP settings in the Supabase Dashboard, or disable email confirmations.';
  }

  // 2. User already registered
  if (
    code === 'user_already_exists' ||
    msg.includes('user already registered') ||
    msg.includes('email already registered') ||
    msg.includes('already exists')
  ) {
    return 'An account with this email address already exists. Please sign in or reset your password.';
  }

  // 3. Database trigger or profile insertion error
  if (
    msg.includes('database error saving new user') ||
    msg.includes('database error')
  ) {
    return 'Database error creating user profile. If you have triggers on auth.users, please check the SQL trigger or contact support@jobmatch.ng.';
  }

  // 4. Invalid credentials
  if (
    code === 'invalid_credentials' ||
    code === 'invalid_grant' ||
    msg.includes('invalid login credentials') ||
    msg.includes('invalid grant') ||
    msg.includes('invalid credentials') ||
    msg.includes('wrong password') ||
    msg.includes('invalid password')
  ) {
    return 'Invalid email or password';
  }

  // 5. Email not confirmed
  if (
    code === 'email_not_confirmed' ||
    msg.includes('email not confirmed') ||
    msg.includes('confirm your email') ||
    msg.includes('unconfirmed')
  ) {
    return 'Your email address has not been confirmed yet. Please check your inbox for the confirmation link or contact support@jobmatch.ng.';
  }

  // 6. User not found (return generic message to prevent account enumeration)
  if (
    code === 'user_not_found' ||
    msg.includes('user not found') ||
    msg.includes('no user found') ||
    msg.includes('no user')
  ) {
    return 'Invalid email or password';
  }

  // 7. Password requirements
  if (msg.includes('password') && (msg.includes('least 6') || msg.includes('short') || msg.includes('weak'))) {
    return 'Password must be at least 6 characters long.';
  }

  // 8. Rate limiting / Too many requests
  if (
    status === 429 ||
    code === 'over_request_rate_limit' ||
    code === 'over_email_send_rate_limit' ||
    msg.includes('rate limit') ||
    msg.includes('too many requests') ||
    msg.includes('over_email_send_rate_limit')
  ) {
    return 'Too many login attempts. Please wait a few minutes before trying again.';
  }

  // 9. Account disabled / banned / suspended
  if (
    code === 'user_banned' ||
    code === 'user_disabled' ||
    msg.includes('banned') ||
    msg.includes('disabled') ||
    msg.includes('suspended')
  ) {
    return 'This account is currently deactivated or suspended. Please contact support@jobmatch.ng.';
  }

  // 10. Network / Connection errors
  if (
    msg.includes('fetch') ||
    msg.includes('network') ||
    msg.includes('connection') ||
    msg.includes('failed to fetch') ||
    msg.includes('timeout') ||
    msg.includes('offline')
  ) {
    return 'Unable to connect to the authentication service. Please check your internet connection and try again.';
  }

  // 11. Supabase API / Project config errors
  if (
    msg.includes('apikey') ||
    msg.includes('api key') ||
    msg.includes('jwt') ||
    msg.includes('tenant') ||
    msg.includes('project not found')
  ) {
    return 'Authentication service configuration error. Please contact platform support.';
  }

  // 12. Default: return clean error message if available
  if (rawMsg && rawMsg.length > 3) {
    return rawMsg;
  }

  return 'Unable to authenticate. Please verify your details and try again.';
}

function createSafeSupabaseClient(): SupabaseClient {
  const targetUrl = isSupabaseConfigured ? supabaseUrl : 'https://placeholder-auth.jobmatch.ng';
  const targetKey = isSupabaseConfigured ? supabaseAnonKey : 'placeholder-anon-key';

  try {
    return createClient(targetUrl, targetKey, {
      auth: {
        persistSession: Boolean(isSupabaseConfigured),
        autoRefreshToken: Boolean(isSupabaseConfigured),
        detectSessionInUrl: Boolean(isSupabaseConfigured),
      },
      realtime: {
        params: {
          eventsPerSecond: 10,
        },
      },
    });
  } catch (err) {
    console.warn('Supabase client initialization notice:', (err as any)?.message || err);
    return createClient('https://placeholder-auth.jobmatch.ng', 'placeholder-anon-key', {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
      },
    });
  }
}

export const supabase: SupabaseClient = createSafeSupabaseClient();

export interface SupabaseHealthStatus {
  connected: boolean;
  tablesReady: boolean;
  error?: string;
  missingTables?: string[];
}

// 1. Health check to detect if SQL migration was executed
export async function checkSupabaseHealth(): Promise<SupabaseHealthStatus> {
  if (!isSupabaseConfigured) {
    return {
      connected: false,
      tablesReady: false,
      error: 'Supabase URL or Anon key is missing.',
    };
  }

  try {
    const { data, error } = await supabase
      .from('service_categories')
      .select('slug')
      .limit(1);

    if (error) {
      // If table is missing in schema cache
      if (error.code === 'PGRST205' || error.message.includes('not find the table')) {
        return {
          connected: true,
          tablesReady: false,
          error: 'Tables not yet migrated. Please run /supabase/production_schema.sql in your Supabase SQL Editor.',
        };
      }
      return {
        connected: true,
        tablesReady: false,
        error: error.message,
      };
    }

    return {
      connected: true,
      tablesReady: true,
    };
  } catch (err: any) {
    return {
      connected: false,
      tablesReady: false,
      error: err?.message || 'Network error connecting to Supabase.',
    };
  }
}

// 2. Storage Helpers
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => resolve(URL.createObjectURL(file));
    reader.readAsDataURL(file);
  });
}

/**
 * Automatically resizes & compresses image files (especially high-res mobile camera snaps)
 * down to under maxSizeBytes (200KB) using HTML5 Canvas.
 */
export async function compressImageFile(file: File, maxSizeBytes: number = 200 * 1024): Promise<File> {
  if (typeof window === 'undefined' || !file || file.size <= maxSizeBytes) {
    return file;
  }

  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };

    img.onload = () => {
      let width = img.width;
      let height = img.height;
      const MAX_DIM = 1200;

      if (width > MAX_DIM || height > MAX_DIM) {
        if (width > height) {
          height = Math.round((height * MAX_DIM) / width);
          width = MAX_DIM;
        } else {
          width = Math.round((width * MAX_DIM) / height);
          height = MAX_DIM;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file);
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      let quality = 0.85;
      const stepQuality = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            if (blob.size <= maxSizeBytes || quality <= 0.2) {
              const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '') + '.jpg', {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              quality -= 0.15;
              stepQuality();
            }
          },
          'image/jpeg',
          quality
        );
      };

      stepQuality();
    };

    img.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
}

export async function uploadJobCompletionPhoto(
  file: File,
  jobId: string,
  professionalId?: string,
  notes?: string
): Promise<string> {
  const MAX_SIZE_BYTES = 200 * 1024; // 200KB limit

  // Automatically compress camera snaps or large image files to under 200KB
  let targetFile = file;
  if (file.size > MAX_SIZE_BYTES) {
    try {
      targetFile = await compressImageFile(file, MAX_SIZE_BYTES);
    } catch (compressErr) {
      console.warn('Image compression fallback notice:', compressErr);
    }
  }

  if (targetFile.size > MAX_SIZE_BYTES) {
    throw new Error(
      `Photo size (${(targetFile.size / 1024).toFixed(1)}KB) exceeds the 200KB limit. Please choose a clearer compressed photo under 200KB.`
    );
  }

  const dataUrl = await fileToDataUrl(targetFile);

  try {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/marketplace/jobs/${jobId}/upload-completion-photo`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        professionalId,
        fileName: targetFile.name,
        fileType: targetFile.type,
        fileSize: targetFile.size,
        completionPhotoUrl: dataUrl,
        completionNotes: notes,
      }),
    });
    const data = await res.json();
    if (data.success && data.photoUrl) {
      return data.photoUrl;
    }
  } catch (err) {
    console.warn('Backend completion photo upload endpoint notice:', err);
  }

  return dataUrl;
}

export async function fetchTemporaryCompletionPhoto(jobId: string): Promise<string | null> {
  try {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/marketplace/jobs/${jobId}/completion-photo`, {
      headers,
    });
    const data = await res.json();
    if (data.success && data.photoData) {
      return data.photoData;
    }
    return null;
  } catch (err) {
    console.warn('Fetch temporary completion photo error:', err);
    return null;
  }
}

export async function discardTemporaryCompletionPhoto(jobId: string): Promise<boolean> {
  try {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/marketplace/jobs/${jobId}/discard-completion-photo`, {
      method: 'POST',
      headers,
    });
    const data = await res.json();
    return Boolean(data.success);
  } catch (err) {
    console.warn('Discard temporary completion photo error:', err);
    return false;
  }
}

export async function uploadJobMedia(file: File, jobId: string): Promise<string> {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${jobId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from('job-media')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (error) {
      console.warn('Supabase storage upload error, saving file as resilient data URL:', error.message);
      return await fileToDataUrl(file);
    }

    const { data: publicUrlData } = supabase.storage
      .from('job-media')
      .getPublicUrl(data.path);

    return publicUrlData.publicUrl;
  } catch (e) {
    console.warn('Storage upload fallback:', e);
    return await fileToDataUrl(file);
  }
}

/**
 * FEATURE 2: Direct Customer Video Upload to Supabase Storage (job-media bucket)
 * Supports MP4, MOV, WebM, 3GP videos directly from customer phone/computer.
 */
export async function uploadJobVideoFile(
  file: File,
  jobId: string,
  onProgress?: (progressPercent: number) => void
): Promise<string> {
  try {
    if (onProgress) onProgress(15);
    const fileExt = file.name.split('.').pop() || 'mp4';
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const storagePath = `${jobId}/videos/${Date.now()}-${sanitizedName}`;

    const contentType = file.type || (fileExt.toLowerCase() === 'mov' ? 'video/quicktime' : 'video/mp4');

    const { data, error } = await supabase.storage
      .from('job-media')
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: true,
        contentType,
      });

    if (onProgress) onProgress(80);

    if (error) {
      console.warn('Supabase video storage notice, using resilient local object URL fallback:', error.message);
      if (onProgress) onProgress(100);
      if (file.size < 5 * 1024 * 1024) {
        return await fileToDataUrl(file);
      }
      return URL.createObjectURL(file);
    }

    const { data: publicUrlData } = supabase.storage
      .from('job-media')
      .getPublicUrl(data.path);

    if (onProgress) onProgress(100);
    return publicUrlData.publicUrl;
  } catch (err) {
    console.warn('Video upload fallback error:', err);
    if (onProgress) onProgress(100);
    if (file.size < 5 * 1024 * 1024) {
      return await fileToDataUrl(file);
    }
    return URL.createObjectURL(file);
  }
}

/**
 * FEATURE 1: Authoritative update to lock verified biometric selfie as official profile photo
 */
export async function updateVerifiedProfilePhoto(proId: string, photoUrl: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({
        avatar_url: photoUrl,
        verified_profile_photo_url: photoUrl,
        is_profile_photo_verified: true,
        verified_photo_captured_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', proId);

    if (error) {
      console.warn('Supabase updateVerifiedProfilePhoto notice:', error.message);
    }

    await fetch(`/api/profiles/photo/${encodeURIComponent(proId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ avatarUrl: photoUrl }),
    }).catch(() => null);

    try {
      const storedProfiles = localStorage.getItem('jobmatch_profiles');
      if (storedProfiles) {
        const parsed = JSON.parse(storedProfiles);
        if (Array.isArray(parsed)) {
          const updated = parsed.map((p: any) =>
            p.id === proId ? { ...p, avatarUrl: photoUrl, avatar_url: photoUrl } : p
          );
          localStorage.setItem('jobmatch_profiles', JSON.stringify(updated));
        }
      }
      const curUser = localStorage.getItem('jobmatch_current_user');
      if (curUser) {
        const parsedUser = JSON.parse(curUser);
        if (parsedUser.id === proId) {
          parsedUser.avatarUrl = photoUrl;
          localStorage.setItem('jobmatch_current_user', JSON.stringify(parsedUser));
        }
      }
    } catch (_) {}

    return !error;
  } catch (err) {
    console.warn('updateVerifiedProfilePhoto error:', err);
    return false;
  }
}

// 3. Auth API
export async function signUpCustomerAccount(payload: {
  email: string;
  password?: string;
  fullName: string;
  phone: string;
  state: string;
  lga: string;
  address?: string;
  avatarUrl?: string;
  registrationSessionId?: string;
  verificationProofToken?: string;
  googleUserId?: string;
}): Promise<SignUpAuthResult> {
  const cleanEmail = payload.email.trim().toLowerCase();
  const pwd = payload.password || 'JobMatch@2026';

  if (!cleanEmail || !cleanEmail.includes('@')) {
    throw new Error('Please enter a valid email address.');
  }
  if (!payload.googleUserId && (!pwd || pwd.length < 6)) {
    throw new Error('Password must be at least 6 characters long.');
  }

  console.info('[AUTH SIGNUP] Starting customer signup', {
    email: maskEmailDiagnostic(cleanEmail),
  });

  let createdUser: any = null;
  let activeSession: any = null;
  let requiresEmailConfirmation = false;
  let lastError: any = null;
  let serverRejectedGate = false;

  // 1. Authoritative server-side registration (enforces Sequential Email OTP + Phone SMS OTP proof)
  try {
    const srvRes = await fetch('/api/auth/register-user', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: cleanEmail,
        password: pwd,
        fullName: payload.fullName.trim(),
        phone: payload.phone.trim(),
        role: 'customer',
        state: payload.state,
        lga: payload.lga,
        address: payload.address || '',
        avatarUrl: payload.avatarUrl || '',
        registrationSessionId: payload.registrationSessionId,
        verificationProofToken: payload.verificationProofToken,
        googleUserId: payload.googleUserId,
      }),
    });

    const srvData = await srvRes.json();
    if (srvData.success && srvData.user) {
      createdUser = srvData.user;
      requiresEmailConfirmation = Boolean(srvData.requiresEmailConfirmation);

      // Establish or preserve client-side Supabase Auth session in browser
      if (!requiresEmailConfirmation) {
        if (payload.googleUserId) {
          const { data: currSess } = await supabase.auth.getSession();
          activeSession = currSess?.session || null;
        } else {
          const { data: signData } = await supabase.auth.signInWithPassword({
            email: cleanEmail,
            password: pwd,
          });
          if (signData?.session) {
            activeSession = signData.session;
          }
        }
      }
    } else {
      if (srvRes.status === 403 || srvRes.status === 409 || srvRes.status === 400) {
        serverRejectedGate = true;
      }
      lastError = new Error(srvData.error || 'Registration verification failed.');
    }
  } catch (srvErr: any) {
    console.warn('[AUTH SIGNUP] Server registration endpoint notice:', srvErr?.message);
    lastError = srvErr;
  }

  // Strictly block client-side fallback if server rejected due to missing sequential verification or duplicate account
  if (serverRejectedGate && lastError) {
    throw lastError;
  }

  // 2. DO NOT FAKE REGISTRATION: If user was NOT created via verified server endpoint, throw an authentic error
  if (!createdUser) {
    const errorMsg = getSupabaseAuthErrorMessage(lastError);
    throw new Error(errorMsg);
  }

  const nowIso = new Date().toISOString();
  const newProfile: Profile = {
    id: createdUser.id,
    role: 'customer',
    fullName: payload.fullName.trim(),
    email: cleanEmail,
    phone: payload.phone.trim(),
    avatarUrl:
      payload.avatarUrl ||
      `https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&auto=format&fit=crop&q=80`,
    state: payload.state,
    lga: payload.lga,
    address: payload.address || '',
    emailVerified: true,
    emailVerifiedAt: nowIso,
    phoneVerified: true,
    phoneVerifiedAt: nowIso,
    onboardingStatus: 'completed',
    professionalApprovalStatus: 'not_applicable',
    accountStatus: 'active',
    verificationStatus: 'verified',
    createdAt: createdUser.created_at || nowIso,
  };

  // 4. Attempt database profile insert with authentic Auth ID
  try {
    await supabase.from('profiles').upsert({
      id: createdUser.id,
      role: 'customer',
      full_name: payload.fullName.trim(),
      email: cleanEmail,
      phone: payload.phone.trim(),
      state: payload.state,
      lga: payload.lga,
      address: payload.address,
      avatar_url: newProfile.avatarUrl,
      email_verified: true,
      email_verified_at: nowIso,
      phone_verified: true,
      phone_verified_at: nowIso,
      onboarding_status: 'completed',
      professional_approval_status: 'not_applicable',
      account_status: 'active',
    });

    await supabase.from('customer_profiles').upsert({
      id: createdUser.id,
      default_address: payload.address,
    });
  } catch (err) {
    console.info('Supabase database customer profile insert notice (handled gracefully):', err);
  }

  return {
    profile: newProfile,
    user: createdUser,
    session: activeSession,
    requiresEmailConfirmation,
  };
}

export async function signUpProfessionalAccount(payload: {
  email: string;
  password?: string;
  fullName: string;
  phone: string;
  businessName: string;
  primaryCategory: string;
  secondaryCategories?: string[];
  serviceAreas?: string[];
  isAvailable?: boolean;
  yearsExperience: number;
  hourlyRate: number;
  idNumber: string;
  state: string;
  lga: string;
  address?: string;
  bio?: string;
  avatarUrl?: string;
  registrationSessionId?: string;
  verificationProofToken?: string;
}): Promise<SignUpAuthResult> {
  const cleanEmail = payload.email.trim().toLowerCase();
  const pwd = payload.password || 'JobMatch@2026';

  if (!cleanEmail || !cleanEmail.includes('@')) {
    throw new Error('Please enter a valid email address.');
  }
  if (!pwd || pwd.length < 6) {
    throw new Error('Password must be at least 6 characters long.');
  }

  console.info('[AUTH SIGNUP] Starting professional signup', {
    email: maskEmailDiagnostic(cleanEmail),
  });

  let createdUser: any = null;
  let activeSession: any = null;
  let requiresEmailConfirmation = false;
  let lastError: any = null;

  // 1. First attempt authoritative server-side registration (uses Admin API when service role key exists)
  try {
    const srvRes = await fetch('/api/auth/register-user', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: cleanEmail,
        password: pwd,
        fullName: payload.fullName.trim(),
        businessName: payload.businessName.trim(),
        phone: payload.phone.trim(),
        role: 'professional',
        primaryCategory: payload.primaryCategory,
        secondaryCategories: payload.secondaryCategories || [],
        serviceAreas: payload.serviceAreas || [payload.lga],
        yearsExperience: payload.yearsExperience,
        hourlyRate: payload.hourlyRate,
        idNumber: payload.idNumber,
        state: payload.state,
        lga: payload.lga,
        address: payload.address || '',
        bio: payload.bio,
        avatarUrl: payload.avatarUrl || '',
        registrationSessionId: payload.registrationSessionId,
        verificationProofToken: payload.verificationProofToken,
      }),
    });

    const srvData = await srvRes.json();
    if (srvData.success && srvData.user) {
      createdUser = srvData.user;
      requiresEmailConfirmation = Boolean(srvData.requiresEmailConfirmation);

      // Establish client-side Supabase Auth session in browser
      if (!requiresEmailConfirmation) {
        const { data: signData } = await supabase.auth.signInWithPassword({
          email: cleanEmail,
          password: pwd,
        });
        if (signData?.session) {
          activeSession = signData.session;
        }
      }
    } else if (srvData.error) {
      lastError = new Error(srvData.error);
    }
  } catch (srvErr: any) {
    console.warn('[AUTH SIGNUP] Server registration endpoint notice for pro:', srvErr?.message);
    lastError = srvErr;
  }

  // 2. If server-side creation did not complete, attempt client-side Supabase Auth signUp
  if (!createdUser) {
    const redirectUrl = `${typeof window !== 'undefined' ? window.location.origin : 'https://jobmatch.ng'}/auth/callback`;
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: cleanEmail,
      password: pwd,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          role: 'professional',
          full_name: payload.fullName.trim(),
          business_name: payload.businessName.trim(),
          phone: payload.phone.trim(),
          primary_category: payload.primaryCategory,
          secondary_categories: payload.secondaryCategories || [],
          service_areas: payload.serviceAreas || [payload.lga],
          years_experience: payload.yearsExperience,
          hourly_rate: payload.hourlyRate,
          id_number: payload.idNumber,
          state: payload.state,
          lga: payload.lga,
          address: payload.address || '',
        },
      },
    });

    if (authData?.user) {
      createdUser = authData.user;
      activeSession = authData.session;
      requiresEmailConfirmation = Boolean(!activeSession && !createdUser.confirmed_at);
    } else if (authError) {
      lastError = authError;
    }
  }

  // 3. DO NOT FAKE REGISTRATION: If user was NOT created in Supabase Auth, throw an authentic error
  if (!createdUser) {
    const errorMsg = getSupabaseAuthErrorMessage(lastError);
    throw new Error(errorMsg);
  }

  const newProfile: Profile = {
    id: createdUser.id,
    role: 'professional',
    fullName: payload.fullName.trim(),
    email: cleanEmail,
    phone: payload.phone.trim(),
    businessName: payload.businessName.trim(),
    primaryCategory: payload.primaryCategory,
    secondaryCategories: payload.secondaryCategories || [],
    yearsExperience: payload.yearsExperience,
    hourlyRate: payload.hourlyRate,
    verificationStatus: 'pending',
    idType: 'NIN (National Identity Management Commission)',
    idNumber: payload.idNumber,
    state: payload.state,
    lga: payload.lga,
    address: payload.address || '',
    bio: payload.bio || `Certified Nigerian artisan with ${payload.yearsExperience} years of experience in ${payload.primaryCategory}.`,
    avatarUrl:
      payload.avatarUrl ||
      `https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&auto=format&fit=crop&q=80`,
    rating: 5.0,
    reviewCount: 0,
    completedJobsCount: 0,
    isAvailable: payload.isAvailable ?? true,
    serviceAreas: payload.serviceAreas && payload.serviceAreas.length > 0 ? payload.serviceAreas : [payload.lga, 'Ikeja', 'Lekki Phase 1'],
    tradeCertifications: ['Federal Trade Test Grade 1 & 2', 'NIMC National ID'],
    createdAt: createdUser.created_at || new Date().toISOString(),
  };

  // 4. Attempt database profile insert with authentic Auth ID
  try {
    await supabase.from('profiles').upsert({
      id: createdUser.id,
      role: 'professional',
      full_name: payload.fullName,
      email: cleanEmail,
      phone: payload.phone,
      state: payload.state,
      lga: payload.lga,
      avatar_url: newProfile.avatarUrl,
    });

    await supabase.from('professional_profiles').upsert({
      id: createdUser.id,
      business_name: payload.businessName,
      primary_category: payload.primaryCategory,
      years_experience: payload.yearsExperience,
      hourly_rate: payload.hourlyRate,
      bio: newProfile.bio,
    });

    await supabase.from('professional_verifications').upsert({
      professional_id: createdUser.id,
      id_type: 'NIN',
      id_number: payload.idNumber,
      verification_status: 'pending',
    });

    if (payload.serviceAreas && payload.serviceAreas.length > 0) {
      const areaInserts = payload.serviceAreas.map((area) => ({
        professional_id: createdUser.id,
        state: payload.state,
        lga: area,
      }));
      await supabase.from('service_areas').upsert(areaInserts);
    }
  } catch (err) {
    console.info('Supabase database professional insert notice (handled gracefully):', err);
  }

  return {
    profile: newProfile,
    user: createdUser,
    session: activeSession,
    requiresEmailConfirmation,
  };
}

export async function loginWithSupabase(email: string, password?: string) {
  if (!email || !email.trim()) {
    return { data: null, error: new Error('Please enter your email address.') };
  }
  if (!password) {
    return { data: null, error: new Error('Please enter your password.') };
  }
  const cleanEmail = email.trim().toLowerCase();
  const { data, error } = await supabase.auth.signInWithPassword({
    email: cleanEmail,
    password,
  });

  if (error) {
    console.warn('[SupabaseAuth Diagnostic] signInWithPassword error:', {
      code: (error as any).code,
      status: (error as any).status,
      message: error.message,
    });
    return { data, error: new Error(getSupabaseAuthErrorMessage(error)) };
  }

  return { data, error: null };
}

export interface GoogleOAuthIntent {
  role?: 'customer' | 'professional';
  authMode: 'login' | 'register';
  returnView?: string;
  initiatedAt: number;
}

const GOOGLE_OAUTH_INTENT_KEY = 'jobmatch_google_oauth_intent';

export function saveGoogleOAuthIntent(intent: Omit<GoogleOAuthIntent, 'initiatedAt'>): void {
  const payload: GoogleOAuthIntent = {
    ...intent,
    initiatedAt: Date.now(),
  };
  try {
    sessionStorage.setItem(GOOGLE_OAUTH_INTENT_KEY, JSON.stringify(payload));
  } catch (_) {}
  try {
    localStorage.setItem(GOOGLE_OAUTH_INTENT_KEY, JSON.stringify(payload));
  } catch (_) {}
}

export function getStoredGoogleOAuthIntent(): GoogleOAuthIntent | null {
  try {
    const raw =
      sessionStorage.getItem(GOOGLE_OAUTH_INTENT_KEY) ||
      localStorage.getItem(GOOGLE_OAUTH_INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GoogleOAuthIntent;
    // Expire stale intents older than 30 minutes
    if (!parsed || typeof parsed.initiatedAt !== 'number' || Date.now() - parsed.initiatedAt > 30 * 60 * 1000) {
      clearStoredGoogleOAuthIntent();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearStoredGoogleOAuthIntent(): void {
  try {
    sessionStorage.removeItem(GOOGLE_OAUTH_INTENT_KEY);
  } catch (_) {}
  try {
    localStorage.removeItem(GOOGLE_OAUTH_INTENT_KEY);
  } catch (_) {}
}

/**
 * Initiates Google OAuth Sign-In / Registration using the existing Supabase Authentication client.
 * Adapts redirectTo for https://jobmatch.ng/ in production and active origin in preview/dev.
 */
export async function signInWithGoogleOAuth(options?: {
  role?: 'customer' | 'professional';
  authMode?: 'login' | 'register';
  returnView?: string;
}): Promise<{ success: boolean; error?: string }> {
  saveGoogleOAuthIntent({
    role: options?.role,
    authMode: options?.authMode || 'login',
    returnView: options?.returnView,
  });

  const isProdHost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'jobmatch.ng' || window.location.hostname === 'www.jobmatch.ng');

  const activeRedirectTo = isProdHost
    ? 'https://jobmatch.ng/'
    : typeof window !== 'undefined' && window.location.origin
    ? `${window.location.origin}/`
    : 'https://jobmatch.ng/';

  try {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: isProdHost ? 'https://jobmatch.ng/' : activeRedirectTo,
        queryParams: {
          access_type: 'offline',
          prompt: 'select_account',
        },
      },
    });

    if (error) {
      clearStoredGoogleOAuthIntent();
      return {
        success: false,
        error: getSupabaseAuthErrorMessage(error),
      };
    }

    return { success: true };
  } catch (err: any) {
    clearStoredGoogleOAuthIntent();
    return {
      success: false,
      error: getSupabaseAuthErrorMessage(err),
    };
  }
}

// Dedicated Supabase Administrator Authentication Flow (Server-Verified)
export async function signInAdmin(email: string, password: string): Promise<{
  user: any;
  session: any;
  profile: Profile;
  mfaRequired?: boolean;
}> {
  const cleanEmail = email.trim().toLowerCase();
  if (!cleanEmail || !password) {
    throw new Error('Please enter both your administrative email and password.');
  }

  // 1. Verify credentials and admin role server-side first
  const srvRes = await fetch('/api/auth/admin/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: cleanEmail,
      password,
    }),
  });

  const srvData = await srvRes.json().catch(() => null);
  if (!srvRes.ok || !srvData?.success) {
    throw new Error(srvData?.message || 'Invalid email or password');
  }

  // 2. Establish authenticated Supabase session on the client after server verification succeeds
  if (srvData.session?.access_token && srvData.session?.refresh_token) {
    await supabase.auth
      .setSession({
        access_token: srvData.session.access_token,
        refresh_token: srvData.session.refresh_token,
      })
      .catch(() => {});
  }

  return {
    user: srvData.user,
    session: srvData.session,
    profile: srvData.profile,
    mfaRequired: false,
  };
}

// Request Password Reset Link via Supabase Auth
export async function requestAdminPasswordReset(email: string): Promise<{ success: boolean; message: string }> {
  const cleanEmail = email.trim().toLowerCase();
  const isProdHost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'jobmatch.ng' || window.location.hostname === 'www.jobmatch.ng');
  const redirectUrl = isProdHost
    ? 'https://jobmatch.ng/reset-password'
    : typeof window !== 'undefined'
    ? `${window.location.origin}/reset-password`
    : 'https://jobmatch.ng/reset-password';
  
  const { error } = await supabase.auth.resetPasswordForEmail(cleanEmail, {
    redirectTo: redirectUrl,
  });

  if (error) {
    throw new Error(error.message || 'Failed to send password reset email.');
  }

  try {
    await fetch('/api/notifications/customer-lifecycle-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventType: 'PASSWORD_RESET',
        customer: {
          email: cleanEmail,
          fullName: 'Administrator',
        },
      }),
    });
  } catch (_) {}

  return {
    success: true,
    message: 'Password reset link sent to your registered administrative email address.',
  };
}

// Sign out from Supabase Auth
export async function signOutAdmin(): Promise<void> {
  try {
    await supabase.auth.signOut();
  } catch (err) {
    console.warn('Sign out warning:', err);
  }
}

/**
 * Fetch and construct complete Profile for an authenticated user
 */
export async function fetchProfileForAuthUser(
  userId: string,
  email?: string,
  fallbackProfiles: Profile[] = []
): Promise<Profile | null> {
  try {
    // 1. Query Supabase profiles table
    const { data: profileRow, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (profileRow) {
      let isProfessional = profileRow.role === 'professional';
      let proDetails: any = null;

      // Self-heal: If profile role was defaulted or out of sync, check professional_profiles
      if (!isProfessional && profileRow.role !== 'admin') {
        const { data: proCheck } = await supabase
          .from('professional_profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();
        if (proCheck) {
          isProfessional = true;
          proDetails = proCheck;
          supabase.from('profiles').update({ role: 'professional' }).eq('id', userId).then(() => {}, () => {});
        }
      }

      // If professional, load additional professional details
      if (isProfessional) {
        if (!proDetails) {
          const { data } = await supabase
            .from('professional_profiles')
            .select('*')
            .eq('id', userId)
            .maybeSingle();
          proDetails = data;
        }

        const { data: verifDetails } = await supabase
          .from('professional_verifications')
          .select('*')
          .eq('professional_id', userId)
          .maybeSingle();

        const { data: serviceAreas } = await supabase
          .from('service_areas')
          .select('lga')
          .eq('professional_id', userId);

        const verifStatus: VerificationStatus =
          verifDetails?.verification_status || profileRow.verification_status || 'pending';
        const hasValidPhone = String(profileRow.phone || '').replace(/\D/g, '').length >= 10;

        const resolvedPrimaryCategory =
          proDetails?.primary_category ||
          proDetails?.trade_category ||
          profileRow.primary_category ||
          profileRow.trade_category ||
          'plumber';

        return {
          id: profileRow.id,
          role: 'professional',
          fullName: profileRow.full_name || 'Artisan',
          email: profileRow.email || email || '',
          phone: profileRow.phone || '',
          avatarUrl: profileRow.avatar_url || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&auto=format&fit=crop&q=80',
          state: profileRow.state || 'Lagos',
          lga: profileRow.lga || 'Ikeja',
          cityArea: profileRow.city_area,
          address: profileRow.address || '',
          bio: proDetails?.bio,
          businessName: proDetails?.business_name,
          primaryCategory: resolvedPrimaryCategory,
          secondaryCategories: proDetails?.secondary_categories || [],
          yearsExperience: proDetails?.years_experience || 3,
          hourlyRate: proDetails?.hourly_rate || 8000,
          isAvailable: proDetails?.is_available ?? (verifStatus === 'verified'),
          verificationStatus: verifStatus,
          professionalApprovalStatus:
            profileRow.professional_approval_status ||
            (verifStatus === 'verified' ? 'approved' : verifStatus === 'rejected' ? 'rejected' : 'pending'),
          emailVerified: true,
          phoneVerified: true,
          onboardingStatus: 'completed',
          accountStatus: profileRow.account_status || (verifStatus === 'verified' ? 'active' : 'pending_verification'),
          isSuspended: Boolean(profileRow.is_suspended),
          idType: verifDetails?.id_type || 'NIN',
          idNumber: verifDetails?.id_number,
          serviceAreas: serviceAreas && serviceAreas.length > 0 ? serviceAreas.map((a: any) => a.lga) : [profileRow.lga || 'Ikeja'],
          createdAt: profileRow.created_at || new Date().toISOString(),
        };
      }

      // Customer or Admin profile
      const isAdminRole = profileRow.role === 'admin';
      const hasValidPhone = String(profileRow.phone || '').replace(/\D/g, '').length >= 10;
      return {
        id: profileRow.id,
        role: (profileRow.role as UserRole) || 'customer',
        fullName: profileRow.full_name || (isAdminRole ? 'Administrator' : 'Customer'),
        email: profileRow.email || email || '',
        phone: profileRow.phone || '',
        avatarUrl: profileRow.avatar_url || (isAdminRole
          ? 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&auto=format&fit=crop&q=80'
          : 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&auto=format&fit=crop&q=80'),
        state: profileRow.state || 'Lagos',
        lga: profileRow.lga || 'Ikeja',
        cityArea: profileRow.city_area,
        address: profileRow.address || '',
        emailVerified: true,
        phoneVerified: true,
        onboardingStatus: 'completed',
        professionalApprovalStatus: 'not_applicable',
        accountStatus: profileRow.account_status || 'active',
        isSuspended: Boolean(profileRow.is_suspended),
        createdAt: profileRow.created_at || new Date().toISOString(),
      };
    }

    // Fallback to authoritative server profile endpoint if direct client query returned null (e.g. RLS or memory store)
    if (userId && typeof fetch === 'function') {
      try {
        const apiRes = await fetch(`/api/profiles/${encodeURIComponent(userId)}`);
        if (apiRes.ok) {
          const apiData = await apiRes.json();
          const p = apiData?.profile;
          if (p && p.id === userId) {
            const isAdminRole = p.role === 'admin';
            const hasValidPhone = String(p.phone || '').replace(/\D/g, '').length >= 10;
            return {
              id: p.id,
              role: p.role || 'customer',
              fullName: p.fullName || p.full_name || (isAdminRole ? 'Administrator' : 'User'),
              email: p.email || email || '',
              phone: p.phone || '',
              avatarUrl:
                p.avatarUrl ||
                p.avatar_url ||
                (isAdminRole
                  ? 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&auto=format&fit=crop&q=80'
                  : p.role === 'professional'
                  ? 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&auto=format&fit=crop&q=80'
                  : 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&auto=format&fit=crop&q=80'),
              state: p.state || 'Lagos',
              lga: p.lga || 'Ikeja',
              cityArea: p.cityArea || p.city_area,
              address: p.address || '',
              bio: p.bio,
              businessName: p.businessName || p.business_name,
              primaryCategory:
                p.primaryCategory ||
                p.primary_category ||
                p.trade_category ||
                (p.role === 'professional' ? 'plumber' : undefined),
              secondaryCategories: p.secondaryCategories || p.secondary_categories || [],
              yearsExperience: p.yearsExperience ?? p.years_experience ?? 3,
              hourlyRate: p.hourlyRate ?? p.hourly_rate ?? 8000,
              isAvailable: p.isAvailable ?? p.is_available ?? true,
              verificationStatus: p.verificationStatus || p.verification_status || 'pending',
              professionalApprovalStatus:
                p.professionalApprovalStatus ||
                p.professional_approval_status ||
                (p.role === 'professional' ? 'pending' : 'not_applicable'),
              emailVerified: true,
              phoneVerified: true,
              onboardingStatus: 'completed',
              accountStatus: p.accountStatus || p.account_status || 'active',
              isSuspended: Boolean(p.isSuspended ?? p.is_suspended),
              serviceAreas: p.serviceAreas || p.service_areas || [p.lga || 'Ikeja'],
              createdAt: p.createdAt || p.created_at || new Date().toISOString(),
            };
          }
        }
      } catch (_) {}
    }
  } catch (err) {
    console.warn('fetchProfileForAuthUser error:', err);
  }

  // Fallback to match strictly by userId in fallbackProfiles (never silently merge by email alone)
  if (userId) {
    const found = fallbackProfiles.find(
      (p) => p.id === userId && !isFakeAccountProfile(p)
    );
    if (found) {
      // Security: Never grant admin role from client fallback profiles
      if (found.role === 'admin') {
        return null;
      }
      return { ...found, id: userId };
    }
  }

  return null;
}

// 3b. Multi-Channel (SMS + Email) OTP Verification API (SMSLive247 & Brevo/Resend Backend)
export interface SendOtpApiResponse {
  success: boolean;
  message: string;
  maskedPhone?: string;
  maskedEmail?: string;
  expiresInSeconds?: number;
  cooldownSeconds?: number;
  recipientFormatted?: string;
  delivery?: {
    sms: boolean;
    email: boolean;
  };
  error?: string;
}

export interface VerifyOtpApiResponse {
  success: boolean;
  message: string;
  verifiedPhone?: string;
  verifiedEmail?: string;
  error?: string;
  remainingAttempts?: number;
}

export async function requestPhoneOtp(
  input: string | { phone: string; email?: string; fullName?: string; userType?: 'customer' | 'professional' }
): Promise<SendOtpApiResponse> {
  const payload = typeof input === 'string' ? { phone: input } : input;
  try {
    const res = await fetch('/api/auth/otp/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    return data;
  } catch (err: any) {
    return {
      success: false,
      message: 'Network error communicating with verification gateway.',
      error: err?.message || 'NETWORK_ERROR',
    };
  }
}

export async function resendPhoneOtp(
  input: string | { phone: string; email?: string; fullName?: string; userType?: 'customer' | 'professional' }
): Promise<SendOtpApiResponse> {
  const payload = typeof input === 'string' ? { phone: input } : input;
  try {
    const res = await fetch('/api/auth/otp/resend', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    return data;
  } catch (err: any) {
    return {
      success: false,
      message: 'Network error communicating with verification gateway.',
      error: err?.message || 'NETWORK_ERROR',
    };
  }
}

export async function verifyPhoneOtp(
  inputPhoneOrOptions: string | { phone?: string; email?: string; otp: string },
  otpCode?: string
): Promise<VerifyOtpApiResponse> {
  const payload =
    typeof inputPhoneOrOptions === 'object'
      ? inputPhoneOrOptions
      : { phone: inputPhoneOrOptions, otp: otpCode || '' };

  try {
    const res = await fetch('/api/auth/otp/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    return data;
  } catch (err: any) {
    return {
      success: false,
      message: 'Network error verifying security code.',
      error: err?.message || 'NETWORK_ERROR',
    };
  }
}

// 4. Marketplace Database Queries
function safeParseJson<T>(val: any, fallback?: T): T | undefined {
  if (!val) return fallback;
  if (typeof val === 'object') return val as T;
  if (typeof val === 'string') {
    try {
      return JSON.parse(val) as T;
    } catch {
      return fallback;
    }
  }
  return fallback;
}

export function cleanContactPhone(val: any): string {
  if (!val || typeof val !== 'string') return '';
  const trimmed = val.trim();
  if (
    !trimmed ||
    trimmed === '0802 000 1122' ||
    trimmed === '+234 800 000 0000' ||
    trimmed === '+234 800 111 2222' ||
    trimmed.toLowerCase() === 'null' ||
    trimmed.toLowerCase() === 'undefined'
  ) {
    return '';
  }
  return trimmed;
}

export function isFakeAccountProfile(rowOrProfile: any): boolean {
  if (!rowOrProfile) return true;
  const id = String(rowOrProfile.id || '').trim();
  const email = String(rowOrProfile.email || '').trim().toLowerCase();
  const fullName = String(rowOrProfile.fullName || rowOrProfile.full_name || '').trim();

  const fakeIds = new Set([
    'pro-1',
    'pro-2',
    'pro-3',
    'pro-4',
    'pro-5',
    'pro-6',
    'cust-1',
    'cust-2',
    'cust-3',
    '401bca28-9dca-4f36-af16-8d86088cd14c',
    '84129fd2-843c-45d3-b07e-f25b7ee522b6',
    '98e4b6a2-84b5-46f5-83fd-aef4413e51c1',
    'b4232c0f-e297-46ff-8a68-bc2f504ff9ad',
    '02027d7a-bf8a-43c1-8138-34be2c5a7212',
    'b421e471-ef73-45a4-95dc-1b354492f95f',
    '21f2d92b-1257-478c-adf3-54c8410412ee',
    '77721c9a-a0da-417e-92e1-ce849cd18b6a',
    '0850036d-fa19-49b2-8e35-9d5ddee955f0',
    'd4b61843-4802-4b3b-97b5-a4830c7283ef',
  ]);
  if (fakeIds.has(id)) return true;

  if (
    email.startsWith('pro_test_') ||
    email.startsWith('customer_test_') ||
    email.startsWith('testcustomer') ||
    email === 'emeka.plumb@jobmatch.ng' ||
    email === 'babajide.electric@jobmatch.ng' ||
    email === 'ibrahim.ac@jobmatch.ng' ||
    email === 'chinedu.carpenter@jobmatch.ng' ||
    email === 'musa.gens@jobmatch.ng' ||
    email === 'osasere.delta@jobmatch.ng' ||
    email === 'folashade@example.com'
  ) {
    return true;
  }

  if (fullName.includes('(Email Fallback Test)')) return true;

  return false;
}

export async function getSupabaseProfiles(): Promise<Profile[] | null> {
  try {
    const [{ data: pRows, error: pErr }, { data: proRows }, { data: verifRows }, { data: areaRows }] = await Promise.all([
      supabase.from('profiles').select('*'),
      supabase.from('professional_profiles').select('*'),
      supabase.from('professional_verifications').select('professional_id, verification_status, id_type, id_number, trade_certifications'),
      supabase.from('service_areas').select('professional_id, lga'),
    ]);

    if (pErr || !Array.isArray(pRows) || pRows.length === 0) return null;

    const proMap = new Map<string, any>();
    (proRows || []).forEach((pr: any) => {
      if (pr?.id) proMap.set(pr.id, pr);
    });

    const verifMap = new Map<string, any>();
    (verifRows || []).forEach((vr: any) => {
      if (vr?.professional_id) verifMap.set(vr.professional_id, vr);
    });

    const areaMap = new Map<string, string[]>();
    (areaRows || []).forEach((ar: any) => {
      if (ar?.professional_id && ar?.lga) {
        const list = areaMap.get(ar.professional_id) || [];
        if (!list.includes(ar.lga)) list.push(ar.lga);
        areaMap.set(ar.professional_id, list);
      }
    });

    return pRows
      .filter((row: any) => !isFakeAccountProfile(row))
      .map((row: any): Profile => {
        const proDetails = proMap.get(row.id);
        const verif = verifMap.get(row.id);
        const isPro = row.role === 'professional' || Boolean(proDetails);
        const areas = areaMap.get(row.id);

      if (isPro) {
        return {
          id: row.id,
          role: 'professional',
          fullName: row.full_name || 'Verified Professional',
          email: row.email || '',
          phone: cleanContactPhone(row.phone),
          avatarUrl: row.avatar_url || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&auto=format&fit=crop&q=80',
          state: row.state || 'Lagos',
          lga: row.lga || 'Ikeja',
          cityArea: row.city_area,
          address: row.address || '',
          bio: proDetails?.bio || '',
          businessName: proDetails?.business_name || (row.full_name ? `${row.full_name} Services` : 'Technical Services'),
          primaryCategory:
            proDetails?.primary_category ||
            proDetails?.trade_category ||
            row.primary_category ||
            row.trade_category ||
            'plumber',
          secondaryCategories: proDetails?.secondary_categories || [],
          yearsExperience: Number(proDetails?.years_experience || 3),
          hourlyRate: Number(proDetails?.hourly_rate || 8000),
          isAvailable: proDetails?.is_available ?? true,
          verificationStatus: verif?.verification_status || row.verification_status || 'verified',
          idType: verif?.id_type || 'NIN',
          idNumber: verif?.id_number,
          tradeCertifications: verif?.trade_certifications || [],
          serviceAreas: areas && areas.length > 0 ? areas : [row.lga || 'Ikeja'],
          rating: Number(proDetails?.rating || 5.0),
          reviewCount: Number(proDetails?.review_count || 0),
          completedJobsCount: Number(proDetails?.completed_jobs_count || 0),
          createdAt: row.created_at || new Date().toISOString(),
        };
      }

      return {
        id: row.id,
        role: (row.role as UserRole) || 'customer',
        fullName: row.full_name || (row.role === 'admin' ? 'Administrator' : 'Customer'),
        email: row.email || '',
        phone: cleanContactPhone(row.phone),
        avatarUrl:
          row.avatar_url ||
          'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&auto=format&fit=crop&q=80',
        state: row.state || 'Lagos',
        lga: row.lga || 'Ikeja',
        cityArea: row.city_area,
        address: row.address || '',
        createdAt: row.created_at || new Date().toISOString(),
      };
    });
  } catch {
    return null;
  }
}

export async function getSupabaseJobs(): Promise<Job[] | null> {
  try {
    const [dbRes, apiRes] = await Promise.all([
      (async () => {
        try {
          const res = await supabase
            .from('jobs')
            .select('*, job_media(*)')
            .order('created_at', { ascending: false });
          if (res.error || !res.data) {
            const fallback = await supabase
              .from('jobs')
              .select('*')
              .order('created_at', { ascending: false });
            if (!fallback.error && fallback.data) return fallback;
          }
          return res;
        } catch {
          return { data: null, error: { message: 'Supabase jobs query failed' } };
        }
      })(),
      fetch('/api/jobs')
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
    ]);

    const mapJobRow = (row: any): Job => {
      const parsedAiAnalysis = safeParseJson<any>(row.ai_analysis || row.aiAnalysis || row.ai_scope_estimate?.ai_analysis);
      const parsedAiScope = safeParseJson<any>(row.ai_scope_estimate || row.aiScopeEstimate);
      const customerMeta = parsedAiScope?.customer_meta || {};
      const custProfile = row.profiles || row.customer || {};
      const proProfile = row.selected_pro || {};

      let customerName =
        custProfile.full_name ||
        customerMeta.customerName ||
        row.customer_name ||
        row.customerName ||
        (row.title && row.title.includes('-') ? row.title.split('-')[0].trim() : 'Customer');
      let customerPhone =
        cleanContactPhone(custProfile.phone) ||
        cleanContactPhone(customerMeta.customerPhone) ||
        cleanContactPhone(customerMeta.customer_phone) ||
        cleanContactPhone(row.customer_phone) ||
        cleanContactPhone(row.customerPhone) ||
        '';

      const customerEmail =
        row.customer_email ||
        row.customerEmail ||
        customerMeta.customerEmail ||
        customerMeta.customer_email ||
        custProfile.email ||
        '';

      let photos: string[] = [];
      let videos: string[] = [];

      if (Array.isArray(row.job_media) && row.job_media.length > 0) {
        photos = row.job_media.filter((m: any) => m.media_type === 'photo').map((m: any) => m.media_url);
        videos = row.job_media.filter((m: any) => m.media_type === 'video').map((m: any) => m.media_url);
      }
      if (photos.length === 0 && Array.isArray(row.photos)) {
        photos = row.photos.filter(Boolean);
      }
      if (videos.length === 0 && Array.isArray(row.videos)) {
        videos = row.videos.filter(Boolean);
      }

      const locMeta = parsedAiScope?.location_meta;
      const lifecycleMeta = parsedAiScope?.lifecycle_meta || {};
      const formattedAddress = row.formatted_address || row.formattedAddress || locMeta?.formattedAddress || row.address;
      const locationLatitude = row.location_latitude != null ? Number(row.location_latitude) : (locMeta?.locationLatitude != null ? Number(locMeta.locationLatitude) : undefined);
      const locationLongitude = row.location_longitude != null ? Number(row.location_longitude) : (locMeta?.locationLongitude != null ? Number(locMeta.locationLongitude) : undefined);
      const cityArea = row.city_area || row.cityArea || locMeta?.cityArea;
      const placeId = row.place_id || row.placeId || locMeta?.placeId;
      const locationLandmark = row.location_landmark || row.locationLandmark || locMeta?.locationLandmark;
      const locationNotes = row.location_notes || row.locationNotes || locMeta?.locationNotes;
      const locationConfidence = row.location_confidence || row.locationConfidence || locMeta?.locationConfidence || 'manual_confirmed';
      const locationConfirmed = row.location_confirmed ?? row.locationConfirmed ?? locMeta?.locationConfirmed ?? true;
      const lgaConfirmed = row.lga_confirmed ?? row.lgaConfirmed ?? locMeta?.lgaConfirmed ?? true;

      const isConfirmedOrReleased = Boolean(
        row.status === 'completed' ||
        row.payment_released ||
        row.confirmed_by_customer ||
        row.customer_confirmed_at ||
        row.completed_by_customer_at ||
        row.completion_status === 'confirmed' ||
        row.completion_status === 'completed' ||
        row.payment_status === 'released' ||
        lifecycleMeta.payment_released ||
        lifecycleMeta.confirmed_by_customer ||
        lifecycleMeta.customer_confirmed_at ||
        lifecycleMeta.completion_status === 'confirmed' ||
        lifecycleMeta.completion_status === 'completed' ||
        lifecycleMeta.payment_status === 'released'
      );

      const isMarkedDoneByPro = Boolean(
        !isConfirmedOrReleased &&
        (row.status === 'completed_by_professional' ||
          row.status === 'professional_completed' ||
          row.completed_by_professional ||
          row.completed_by_professional_at ||
          row.professional_completed_at ||
          row.completion_status === 'completed_by_professional' ||
          row.completion_status === 'awaiting_customer_confirmation' ||
          lifecycleMeta.completed_by_professional ||
          lifecycleMeta.professional_completed_at ||
          lifecycleMeta.completion_status === 'completed_by_professional' ||
          lifecycleMeta.completion_status === 'awaiting_customer_confirmation')
      );

      const isExpired = Boolean(
        row.status === 'expired' ||
          lifecycleMeta.status === 'expired' ||
          row.expired_at ||
          lifecycleMeta.expired_at ||
          lifecycleMeta.cancellation_stage === 'expired_24h'
      );

      const isEscrowPaid = Boolean(
        !isConfirmedOrReleased &&
          (row.payment_status === 'held_in_escrow' ||
            row.paymentStatus === 'held_in_escrow' ||
            row.payment_status === 'paid' ||
            row.paymentStatus === 'paid' ||
            lifecycleMeta.payment_status === 'held_in_escrow' ||
            lifecycleMeta.payment_status === 'paid' ||
            row.escrow_held === true ||
            lifecycleMeta.escrow_held === true)
      );

      const resolvedStatus = isConfirmedOrReleased
        ? 'completed'
        : isExpired
        ? 'expired'
        : row.status === 'cancelled' || lifecycleMeta.status === 'cancelled'
        ? 'cancelled'
        : row.status === 'disputed' || lifecycleMeta.status === 'disputed'
        ? 'disputed'
        : isMarkedDoneByPro
        ? 'completed_by_professional'
        : isEscrowPaid
        ? 'in_progress'
        : row.status || 'open';

      const resolvedPaymentStatus = isConfirmedOrReleased
        ? 'released'
        : isEscrowPaid
        ? 'held_in_escrow'
        : row.payment_status || row.paymentStatus || lifecycleMeta.payment_status;

      const resolvedCompletionPhotoUrl =
        row.completion_photo_url ||
        row.completionPhotoUrl ||
        lifecycleMeta.completion_photo_url ||
        lifecycleMeta.temp_completion_photo_url;

      const resolvedCustomerId = row.customer_id || row.customerId || customerMeta.customerId || '401bca28-9dca-4f36-af16-8d86088cd14c';
      const rawCreatedAt = row.created_at || row.createdAt || new Date().toISOString();
      const rawUpdatedAt = row.updated_at || row.updatedAt || new Date().toISOString();

      return {
        id: row.id,
        customerId: resolvedCustomerId,
        customer_id: resolvedCustomerId,
        customerName,
        customer_name: customerName,
        customerPhone,
        customer_phone: customerPhone,
        customerEmail,
        customer_email: customerEmail,
        categorySlug: row.category_slug || row.categorySlug || 'other',
        category_slug: row.category_slug || row.categorySlug || 'other',
        categoryName: row.category_name || row.categoryName || 'Service',
        category_name: row.category_name || row.categoryName || 'Service',
        normalizedOccupation: row.normalized_occupation || parsedAiAnalysis?.normalized_occupation,
        parentFamily: row.parent_family || parsedAiAnalysis?.parent_family,
        title: row.title || 'Service Request',
        description: row.description || parsedAiAnalysis?.problem_summary || 'Service request submitted by customer.',
        state: row.state || 'Lagos',
        lga: row.lga || 'Ikeja',
        cityArea: cityArea,
        address: row.address || `${row.lga || 'Ikeja'}, ${row.state || 'Lagos'}`,
        formattedAddress: formattedAddress,
        locationLatitude: locationLatitude,
        locationLongitude: locationLongitude,
        placeId: placeId,
        locationLandmark: locationLandmark,
        locationNotes: locationNotes,
        locationConfidence: locationConfidence,
        locationConfirmed: locationConfirmed,
        lgaConfirmed: lgaConfirmed,
        urgency: ['emergency', 'within_48h', 'flexible'].includes(row.urgency) ? row.urgency : 'within_48h',
        budgetEstimate: Number(row.budget_estimate || row.budgetEstimate || 0),
        budget_estimate: Number(row.budget_estimate || row.budgetEstimate || 0),
        createdAt: rawCreatedAt,
        created_at: rawCreatedAt,
        updatedAt: rawUpdatedAt,
        updated_at: rawUpdatedAt,
        status: resolvedStatus,
        selectedProfessionalId: row.selected_professional_id || row.selectedProfessionalId || lifecycleMeta.selected_professional_id,
        selectedProfessionalName: proProfile.full_name || row.selected_professional_name || row.selectedProfessionalName || lifecycleMeta.selected_professional_name,
        selectedProfessionalPhone:
          cleanContactPhone(proProfile.phone) ||
          cleanContactPhone(row.selected_professional_phone) ||
          cleanContactPhone(row.selectedProfessionalPhone) ||
          cleanContactPhone(lifecycleMeta.selected_professional_phone) ||
          undefined,
        photos,
        videos,
        aiAnalysis: parsedAiAnalysis as AIJobAnalysis | undefined,
        aiScopeEstimate: parsedAiScope as AIEstimateResult | undefined,
        quotesCount: Number(row.quotes_count || row.quotesCount || 0),
        completionPhotoUrl: resolvedCompletionPhotoUrl,
        completionNotes: row.completion_notes || row.completionNotes || lifecycleMeta.completion_notes,
        completionStatus: isConfirmedOrReleased
          ? 'confirmed'
          : isMarkedDoneByPro
          ? 'completed_by_professional'
          : row.completion_status || row.completionStatus || lifecycleMeta.completion_status,
        completedByProfessionalAt: row.completed_by_professional_at || row.professional_completed_at || row.completedByProfessionalAt || lifecycleMeta.professional_completed_at,
        completedByCustomerAt: row.completed_by_customer_at || row.customer_confirmed_at || row.completedByCustomerAt || lifecycleMeta.customer_confirmed_at,
        completionConfirmedAt: row.completion_confirmed_at || row.customer_confirmed_at || row.completionConfirmedAt || lifecycleMeta.customer_confirmed_at,
        professional_completed_at: row.professional_completed_at || row.completed_by_professional_at || lifecycleMeta.professional_completed_at,
        customer_confirmed_at: row.customer_confirmed_at || row.completed_by_customer_at || lifecycleMeta.customer_confirmed_at,
        completed_at: row.completed_at || lifecycleMeta.completed_at,
        completed_by_professional: Boolean(isMarkedDoneByPro || isConfirmedOrReleased || row.completed_by_professional || row.completed_by_professional_at || row.professional_completed_at || lifecycleMeta.completed_by_professional || lifecycleMeta.professional_completed_at),
        confirmed_by_customer: Boolean(isConfirmedOrReleased || row.confirmed_by_customer || row.customer_confirmed_at || row.completed_by_customer_at || lifecycleMeta.confirmed_by_customer || lifecycleMeta.customer_confirmed_at),
        payment_released: Boolean(isConfirmedOrReleased || row.payment_released || lifecycleMeta.payment_released || lifecycleMeta.payment_status === 'released'),
        payment_released_at: row.payment_released_at || lifecycleMeta.payment_released_at,
        payment_status: resolvedPaymentStatus,
        paymentStatus: resolvedPaymentStatus as any,
        escrow_held: isConfirmedOrReleased ? false : Boolean(isEscrowPaid || row.escrow_held || lifecycleMeta.escrow_held || (lifecycleMeta.payment_status === 'held_in_escrow')),
        has_completion_photo: Boolean(row.has_completion_photo ?? lifecycleMeta.has_completion_photo ?? resolvedCompletionPhotoUrl),
        completion_photo_viewed: Boolean(row.completion_photo_viewed ?? lifecycleMeta.completion_photo_viewed),
        professional_earnings: (row.professional_earnings ?? lifecycleMeta.professional_earnings) ? Number(row.professional_earnings ?? lifecycleMeta.professional_earnings) : undefined,
        transaction_id: row.transaction_id || lifecycleMeta.transaction_id,
        payment_reference: row.payment_reference || lifecycleMeta.payment_reference,
        customer_feedback: row.customer_feedback || lifecycleMeta.customer_feedback,
        customer_rating: (row.customer_rating ?? lifecycleMeta.customer_rating) ? Number(row.customer_rating ?? lifecycleMeta.customer_rating) : undefined,
        customer_review: row.customer_review || lifecycleMeta.customer_review,
        cancelled_at: row.cancelled_at ?? lifecycleMeta.cancelled_at ?? null,
        cancelled_by: row.cancelled_by ?? lifecycleMeta.cancelled_by ?? null,
        cancellation_reason: row.cancellation_reason ?? lifecycleMeta.cancellation_reason ?? null,
        cancellation_stage: row.cancellation_stage ?? lifecycleMeta.cancellation_stage ?? null,
        no_show_reported_at: row.no_show_reported_at ?? lifecycleMeta.no_show_reported_at ?? null,
        no_show_reason: row.no_show_reason ?? lifecycleMeta.no_show_reason ?? null,
        no_show_status: row.no_show_status ?? lifecycleMeta.no_show_status ?? null,
        no_show_professional_id: row.no_show_professional_id ?? lifecycleMeta.no_show_professional_id ?? null,
        reassigned_at: row.reassigned_at ?? lifecycleMeta.reassigned_at ?? null,
        reassigned_from_professional_id: row.reassigned_from_professional_id ?? lifecycleMeta.reassigned_from_professional_id ?? null,
        reassignment_count: row.reassignment_count ?? lifecycleMeta.reassignment_count ?? 0,
        previous_professional_ids: row.previous_professional_ids ?? lifecycleMeta.previous_professional_ids ?? [],
        escrow_frozen: Boolean(row.escrow_frozen ?? lifecycleMeta.escrow_frozen ?? false),
        dispute_id: row.dispute_id ?? lifecycleMeta.dispute_id ?? null,
        scheduled_arrival_at: row.scheduled_arrival_at ?? lifecycleMeta.scheduled_arrival_at ?? null,
        professional_arrived_at: row.professional_arrived_at ?? lifecycleMeta.professional_arrived_at ?? null,
        professional_arrived: Boolean(
          row.professional_arrived ??
          lifecycleMeta.professional_arrived ??
          row.professional_arrived_at ??
          lifecycleMeta.professional_arrived_at ??
          row.customer_confirmed_arrival_at ??
          lifecycleMeta.customer_confirmed_arrival_at
        ),
        work_started_at: row.work_started_at ?? lifecycleMeta.work_started_at ?? null,
        rescheduled_at: row.rescheduled_at ?? lifecycleMeta.rescheduled_at ?? null,
        rescheduled_reason: row.rescheduled_reason ?? lifecycleMeta.rescheduled_reason ?? null,
        delay_reported_at: row.delay_reported_at ?? lifecycleMeta.delay_reported_at ?? null,
        delay_minutes: row.delay_minutes != null ? Number(row.delay_minutes) : (lifecycleMeta.delay_minutes != null ? Number(lifecycleMeta.delay_minutes) : 0),
        delay_reason: row.delay_reason ?? lifecycleMeta.delay_reason ?? null,
        estimated_arrival_at: row.estimated_arrival_at ?? lifecycleMeta.estimated_arrival_at ?? null,
        grace_period_minutes: row.grace_period_minutes != null ? Number(row.grace_period_minutes) : (lifecycleMeta.grace_period_minutes != null ? Number(lifecycleMeta.grace_period_minutes) : 15),
        auto_reassigned: Boolean(row.auto_reassigned ?? lifecycleMeta.auto_reassigned),
        auto_reassigned_at: row.auto_reassigned_at ?? lifecycleMeta.auto_reassigned_at ?? null,
        auto_reassignment_attempts: row.auto_reassignment_attempts != null ? Number(row.auto_reassignment_attempts) : (lifecycleMeta.auto_reassignment_attempts != null ? Number(lifecycleMeta.auto_reassignment_attempts) : 0),
        auto_reassignment_last_attempt_at: row.auto_reassignment_last_attempt_at ?? lifecycleMeta.auto_reassignment_last_attempt_at ?? null,
        expired_at: row.expired_at ?? lifecycleMeta.expired_at ?? null,
        accepted_at: row.accepted_at ?? lifecycleMeta.accepted_at ?? row.assigned_at ?? lifecycleMeta.assigned_at ?? null,
        eta_agreement_deadline_at: row.eta_agreement_deadline_at ?? lifecycleMeta.eta_agreement_deadline_at ?? null,
        proposed_eta_at: row.proposed_eta_at ?? lifecycleMeta.proposed_eta_at ?? null,
        proposed_eta_minutes: row.proposed_eta_minutes != null ? Number(row.proposed_eta_minutes) : (lifecycleMeta.proposed_eta_minutes != null ? Number(lifecycleMeta.proposed_eta_minutes) : null),
        agreed_eta_at: row.agreed_eta_at ?? lifecycleMeta.agreed_eta_at ?? null,
        eta_confirmed_by_customer_at: row.eta_confirmed_by_customer_at ?? lifecycleMeta.eta_confirmed_by_customer_at ?? null,
        eta_status: row.eta_status ?? lifecycleMeta.eta_status ?? null,
        customer_confirmed_arrival_at: row.customer_confirmed_arrival_at ?? lifecycleMeta.customer_confirmed_arrival_at ?? null,
        overdue_notified_at: row.overdue_notified_at ?? lifecycleMeta.overdue_notified_at ?? null,
      } as Job;
    };

    const mergedMap = new Map<string, Job>();
    if (apiRes?.success && Array.isArray(apiRes.jobs)) {
      apiRes.jobs.forEach((r: any) => {
        if (r?.id) mergedMap.set(r.id, mapJobRow(r));
      });
    }
    if (!dbRes.error && Array.isArray(dbRes.data)) {
      dbRes.data.forEach((r: any) => {
        if (r?.id) {
          const existing = mergedMap.get(r.id);
          const mapped = mapJobRow(r);
          if (existing) {
            const mergedPaymentReleased = Boolean(mapped.payment_released || existing.payment_released);
            const mergedConfirmedByCustomer = Boolean(mapped.confirmed_by_customer || existing.confirmed_by_customer || mergedPaymentReleased);
            const mergedCompletedByPro = Boolean(mapped.completed_by_professional || existing.completed_by_professional || mergedConfirmedByCustomer);
            const mergedEscrowPaid = Boolean(
              !mergedPaymentReleased &&
                ((mapped as any).payment_status === 'held_in_escrow' ||
                  (existing as any).payment_status === 'held_in_escrow' ||
                  mapped.paymentStatus === 'held_in_escrow' ||
                  existing.paymentStatus === 'held_in_escrow' ||
                  mapped.escrow_held ||
                  existing.escrow_held)
            );
            const mergedStatus =
              existing.status === 'completed' || mapped.status === 'completed' || mergedConfirmedByCustomer
                ? 'completed'
                : existing.status === 'completed_by_professional' || mapped.status === 'completed_by_professional' || mergedCompletedByPro
                ? 'completed_by_professional'
                : mergedEscrowPaid || existing.status === 'in_progress' || mapped.status === 'in_progress'
                ? 'in_progress'
                : mapped.status !== 'open'
                ? mapped.status
                : existing.status;

            const mergedPayStatus = mergedPaymentReleased
              ? 'released'
              : mergedEscrowPaid
              ? 'held_in_escrow'
              : (mapped as any).payment_status || (existing as any).payment_status;

            mergedMap.set(r.id, {
              ...existing,
              ...mapped,
              customerName:
                mapped.customerName && mapped.customerName !== 'Customer'
                  ? mapped.customerName
                  : existing.customerName || 'Customer',
              customerPhone:
                cleanContactPhone(mapped.customerPhone) ||
                cleanContactPhone(existing.customerPhone) ||
                '',
              status: mergedStatus,
              selectedProfessionalId: mapped.selectedProfessionalId || existing.selectedProfessionalId,
              selectedProfessionalName: mapped.selectedProfessionalName || existing.selectedProfessionalName,
              selectedProfessionalPhone:
                cleanContactPhone(mapped.selectedProfessionalPhone) ||
                cleanContactPhone(existing.selectedProfessionalPhone) ||
                undefined,
              completionStatus:
                mergedStatus === 'completed'
                  ? 'confirmed'
                  : mergedStatus === 'completed_by_professional'
                  ? 'completed_by_professional'
                  : mapped.completionStatus || existing.completionStatus,
              completed_by_professional: mergedCompletedByPro,
              confirmed_by_customer: mergedConfirmedByCustomer,
              has_completion_photo: Boolean((mapped as any).has_completion_photo || (existing as any).has_completion_photo),
              completion_photo_viewed: Boolean((mapped as any).completion_photo_viewed || (existing as any).completion_photo_viewed),
              completionPhotoUrl: mapped.completionPhotoUrl || existing.completionPhotoUrl,
              payment_released: mergedPaymentReleased,
              payment_status: mergedPayStatus,
              paymentStatus: mergedPayStatus as any,
              escrow_held: mergedPaymentReleased ? false : Boolean(mergedEscrowPaid || mapped.escrow_held || existing.escrow_held),
              customer_rating: (mapped as any).customer_rating || (existing as any).customer_rating,
              customer_review: (mapped as any).customer_review || (existing as any).customer_review,
              photos: mapped.photos.length > 0 ? mapped.photos : existing.photos,
              videos: mapped.videos && mapped.videos.length > 0 ? mapped.videos : existing.videos,
            } as Job);
          } else {
            mergedMap.set(r.id, mapped);
          }
        }
      });
    }

    if (mergedMap.size === 0 && dbRes.error && !apiRes?.success) return null;
    return Array.from(mergedMap.values()).sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  } catch {
    return null;
  }
}

export async function getSupabaseJobById(jobId: string): Promise<Job | null> {
  try {
    const { data, error } = await supabase
      .from('jobs')
      .select('*, job_media(*), profiles:customer_id(full_name, phone, avatar_url), selected_pro:selected_professional_id(full_name, phone, avatar_url)')
      .eq('id', jobId)
      .maybeSingle();

    if (error || !data) return null;

    const parsedAiAnalysis = safeParseJson<any>(data.ai_analysis || data.aiAnalysis || data.ai_scope_estimate?.ai_analysis);
    const parsedAiScope = safeParseJson<any>(data.ai_scope_estimate || data.aiScopeEstimate);
    const customerMeta = parsedAiScope?.customer_meta || {};
    const custProfile = data.profiles || data.customer || {};
    const proProfile = data.selected_pro || {};

    let customerName =
      custProfile.full_name ||
      customerMeta.customerName ||
      data.customer_name ||
      data.customerName ||
      (data.title?.includes('-') ? data.title.split('-')[0].trim() : 'Customer');
    let customerPhone =
      cleanContactPhone(custProfile.phone) ||
      cleanContactPhone(customerMeta.customerPhone) ||
      cleanContactPhone(customerMeta.customer_phone) ||
      cleanContactPhone(data.customer_phone) ||
      cleanContactPhone(data.customerPhone);

    if ((!customerPhone || customerName === 'Customer') && data.customer_id) {
      try {
        const { data: fetchedCust } = await supabase
          .from('profiles')
          .select('full_name, phone')
          .eq('id', data.customer_id)
          .maybeSingle();

        if (fetchedCust) {
          if (fetchedCust.full_name) customerName = fetchedCust.full_name;
          if (cleanContactPhone(fetchedCust.phone)) customerPhone = cleanContactPhone(fetchedCust.phone);
        }
      } catch (_) {}
    }

    let photos: string[] = [];
    let videos: string[] = [];

    if (Array.isArray(data.job_media) && data.job_media.length > 0) {
      photos = data.job_media.filter((m: any) => m.media_type === 'photo').map((m: any) => m.media_url);
      videos = data.job_media.filter((m: any) => m.media_type === 'video').map((m: any) => m.media_url);
    }
    if (photos.length === 0 && Array.isArray(data.photos)) {
      photos = data.photos.filter(Boolean);
    }
    if (videos.length === 0 && Array.isArray(data.videos)) {
      videos = data.videos.filter(Boolean);
    }

    const locMeta = parsedAiScope?.location_meta;
    const lifecycleMeta = parsedAiScope?.lifecycle_meta || {};
    const formattedAddress = data.formatted_address || data.formattedAddress || locMeta?.formattedAddress || data.address;
    const locationLatitude = data.location_latitude != null ? Number(data.location_latitude) : (locMeta?.locationLatitude != null ? Number(locMeta.locationLatitude) : undefined);
    const locationLongitude = data.location_longitude != null ? Number(data.location_longitude) : (locMeta?.locationLongitude != null ? Number(locMeta.locationLongitude) : undefined);
    const cityArea = data.city_area || data.cityArea || locMeta?.cityArea;
    const placeId = data.place_id || data.placeId || locMeta?.placeId;
    const locationLandmark = data.location_landmark || data.locationLandmark || locMeta?.locationLandmark;
    const locationNotes = data.location_notes || data.locationNotes || locMeta?.locationNotes;
    const locationConfidence = data.location_confidence || data.locationConfidence || locMeta?.locationConfidence || 'manual_confirmed';
    const locationConfirmed = data.location_confirmed ?? data.locationConfirmed ?? locMeta?.locationConfirmed ?? true;
    const lgaConfirmed = data.lga_confirmed ?? data.lgaConfirmed ?? locMeta?.lgaConfirmed ?? true;

    return {
      id: data.id,
      customerId: data.customer_id || data.customerId,
      customerName,
      customerPhone,
      categorySlug: data.category_slug || data.categorySlug || 'other',
      categoryName: data.category_name || data.categoryName || 'Service',
      normalizedOccupation: data.normalized_occupation || parsedAiAnalysis?.normalized_occupation,
      parentFamily: data.parent_family || parsedAiAnalysis?.parent_family,
      title: data.title || 'Service Request',
      description: data.description || parsedAiAnalysis?.problem_summary || 'Service request submitted by customer.',
      state: data.state || 'Lagos',
      lga: data.lga || 'Ikeja',
      cityArea: cityArea,
      address: data.address || `${data.lga || 'Ikeja'}, ${data.state || 'Lagos'}`,
      formattedAddress: formattedAddress,
      locationLatitude: locationLatitude,
      locationLongitude: locationLongitude,
      placeId: placeId,
      locationLandmark: locationLandmark,
      locationNotes: locationNotes,
      locationConfidence: locationConfidence,
      locationConfirmed: locationConfirmed,
      lgaConfirmed: lgaConfirmed,
      urgency: ['emergency', 'within_48h', 'flexible'].includes(data.urgency) ? data.urgency : 'within_48h',
      budgetEstimate: Number(data.budget_estimate || data.budgetEstimate || 0),
      status:
        lifecycleMeta.status === 'disputed' || data.status === 'disputed'
          ? 'disputed'
          : lifecycleMeta.status === 'expired' || data.status === 'expired' || data.expired_at || lifecycleMeta.expired_at || lifecycleMeta.cancellation_stage === 'expired_24h'
          ? 'expired'
          : lifecycleMeta.status === 'cancelled' || data.status === 'cancelled'
          ? 'cancelled'
          : data.status === 'completed' || lifecycleMeta.status === 'completed' || lifecycleMeta.payment_released === true || lifecycleMeta.payment_status === 'released'
          ? 'completed'
          : Boolean(data.completed_by_professional || lifecycleMeta.completed_by_professional)
          ? 'completed_by_professional'
          : data.payment_status === 'held_in_escrow' || lifecycleMeta.payment_status === 'held_in_escrow' || lifecycleMeta.payment_status === 'paid'
          ? 'in_progress'
          : data.status || 'open',
      selectedProfessionalId: data.selected_professional_id || data.selectedProfessionalId || lifecycleMeta.selected_professional_id,
      selectedProfessionalName: proProfile.full_name || data.selected_professional_name || data.selectedProfessionalName || lifecycleMeta.selected_professional_name,
      selectedProfessionalPhone:
        cleanContactPhone(proProfile.phone) ||
        cleanContactPhone(data.selected_professional_phone) ||
        cleanContactPhone(data.selectedProfessionalPhone) ||
        cleanContactPhone(lifecycleMeta.selected_professional_phone) ||
        undefined,
      photos,
      videos,
      aiAnalysis: parsedAiAnalysis as AIJobAnalysis | undefined,
      aiScopeEstimate: parsedAiScope as AIEstimateResult | undefined,
      quotesCount: Number(data.quotes_count || data.quotesCount || 0),
      completionPhotoUrl: data.completion_photo_url || data.completionPhotoUrl || lifecycleMeta.completion_photo_url || lifecycleMeta.temp_completion_photo_url,
      completionNotes: data.completion_notes || data.completionNotes || lifecycleMeta.completion_notes,
      completionStatus: data.completion_status || data.completionStatus || lifecycleMeta.completion_status,
      completedByProfessionalAt: data.completed_by_professional_at || data.professional_completed_at || data.completedByProfessionalAt || lifecycleMeta.professional_completed_at,
      completedByCustomerAt: data.completed_by_customer_at || data.customer_confirmed_at || data.completedByCustomerAt || lifecycleMeta.customer_confirmed_at,
      completionConfirmedAt: data.completion_confirmed_at || data.customer_confirmed_at || data.completionConfirmedAt || lifecycleMeta.customer_confirmed_at,
      professional_completed_at: data.professional_completed_at || data.completed_by_professional_at || lifecycleMeta.professional_completed_at,
      customer_confirmed_at: data.customer_confirmed_at || data.completed_by_customer_at || lifecycleMeta.customer_confirmed_at,
      completed_at: data.completed_at || lifecycleMeta.completed_at,
      completed_by_professional: Boolean(data.completed_by_professional || data.completed_by_professional_at || data.professional_completed_at || lifecycleMeta.completed_by_professional || lifecycleMeta.professional_completed_at),
      confirmed_by_customer: Boolean(data.confirmed_by_customer || data.customer_confirmed_at || data.completed_by_customer_at || lifecycleMeta.confirmed_by_customer || lifecycleMeta.customer_confirmed_at),
      payment_released: Boolean(data.payment_released || lifecycleMeta.payment_released || lifecycleMeta.payment_status === 'released'),
      payment_released_at: data.payment_released_at || lifecycleMeta.payment_released_at,
      payment_status: data.payment_status || lifecycleMeta.payment_status,
      paymentStatus: (data.payment_status || lifecycleMeta.payment_status) as any,
      escrow_held: Boolean(data.escrow_held ?? lifecycleMeta.escrow_held ?? (lifecycleMeta.payment_status === 'held_in_escrow')),
      has_completion_photo: Boolean(data.has_completion_photo ?? lifecycleMeta.has_completion_photo ?? lifecycleMeta.completion_photo_url ?? lifecycleMeta.temp_completion_photo_url),
      completion_photo_viewed: Boolean(data.completion_photo_viewed ?? lifecycleMeta.completion_photo_viewed),
      professional_earnings: (data.professional_earnings ?? lifecycleMeta.professional_earnings) ? Number(data.professional_earnings ?? lifecycleMeta.professional_earnings) : undefined,
      transaction_id: data.transaction_id || lifecycleMeta.transaction_id,
      payment_reference: data.payment_reference || lifecycleMeta.payment_reference,
      customer_feedback: data.customer_feedback || lifecycleMeta.customer_feedback,
      customer_rating: (data.customer_rating ?? lifecycleMeta.customer_rating) ? Number(data.customer_rating ?? lifecycleMeta.customer_rating) : undefined,
      customer_review: data.customer_review || lifecycleMeta.customer_review,
      cancelled_at: data.cancelled_at ?? lifecycleMeta.cancelled_at ?? null,
      cancelled_by: data.cancelled_by ?? lifecycleMeta.cancelled_by ?? null,
      cancellation_reason: data.cancellation_reason ?? lifecycleMeta.cancellation_reason ?? null,
      cancellation_stage: data.cancellation_stage ?? lifecycleMeta.cancellation_stage ?? null,
      no_show_reported_at: data.no_show_reported_at ?? lifecycleMeta.no_show_reported_at ?? null,
      no_show_reason: data.no_show_reason ?? lifecycleMeta.no_show_reason ?? null,
      no_show_status: data.no_show_status ?? lifecycleMeta.no_show_status ?? null,
      no_show_professional_id: data.no_show_professional_id ?? lifecycleMeta.no_show_professional_id ?? null,
      reassigned_at: data.reassigned_at ?? lifecycleMeta.reassigned_at ?? null,
      reassigned_from_professional_id: data.reassigned_from_professional_id ?? lifecycleMeta.reassigned_from_professional_id ?? null,
      reassignment_count: data.reassignment_count ?? lifecycleMeta.reassignment_count ?? 0,
      previous_professional_ids: data.previous_professional_ids ?? lifecycleMeta.previous_professional_ids ?? [],
      escrow_frozen: Boolean(data.escrow_frozen ?? lifecycleMeta.escrow_frozen ?? false),
      dispute_id: data.dispute_id ?? lifecycleMeta.dispute_id ?? null,
      scheduled_arrival_at: data.scheduled_arrival_at ?? lifecycleMeta.scheduled_arrival_at ?? null,
      professional_arrived_at: data.professional_arrived_at ?? lifecycleMeta.professional_arrived_at ?? null,
      professional_arrived: Boolean(data.professional_arrived ?? lifecycleMeta.professional_arrived ?? data.professional_arrived_at ?? lifecycleMeta.professional_arrived_at),
      work_started_at: data.work_started_at ?? lifecycleMeta.work_started_at ?? null,
      rescheduled_at: data.rescheduled_at ?? lifecycleMeta.rescheduled_at ?? null,
      rescheduled_reason: data.rescheduled_reason ?? lifecycleMeta.rescheduled_reason ?? null,
      delay_reported_at: data.delay_reported_at ?? lifecycleMeta.delay_reported_at ?? null,
      delay_minutes: data.delay_minutes != null ? Number(data.delay_minutes) : (lifecycleMeta.delay_minutes != null ? Number(lifecycleMeta.delay_minutes) : undefined),
      delay_reason: data.delay_reason ?? lifecycleMeta.delay_reason ?? null,
      estimated_arrival_at: data.estimated_arrival_at ?? lifecycleMeta.estimated_arrival_at ?? null,
      grace_period_minutes: data.grace_period_minutes != null ? Number(data.grace_period_minutes) : (lifecycleMeta.grace_period_minutes != null ? Number(lifecycleMeta.grace_period_minutes) : 15),
      auto_reassigned: Boolean(data.auto_reassigned ?? lifecycleMeta.auto_reassigned),
      auto_reassigned_at: data.auto_reassigned_at ?? lifecycleMeta.auto_reassigned_at ?? null,
      auto_reassignment_attempts: data.auto_reassignment_attempts != null ? Number(data.auto_reassignment_attempts) : (lifecycleMeta.auto_reassignment_attempts != null ? Number(lifecycleMeta.auto_reassignment_attempts) : undefined),
      auto_reassignment_last_attempt_at: data.auto_reassignment_last_attempt_at ?? lifecycleMeta.auto_reassignment_last_attempt_at ?? null,
      expired_at: data.expired_at ?? lifecycleMeta.expired_at ?? null,
      accepted_at: data.accepted_at ?? lifecycleMeta.accepted_at ?? data.assigned_at ?? lifecycleMeta.assigned_at ?? null,
      eta_agreement_deadline_at: data.eta_agreement_deadline_at ?? lifecycleMeta.eta_agreement_deadline_at ?? null,
      proposed_eta_at: data.proposed_eta_at ?? lifecycleMeta.proposed_eta_at ?? null,
      proposed_eta_minutes: data.proposed_eta_minutes != null ? Number(data.proposed_eta_minutes) : (lifecycleMeta.proposed_eta_minutes != null ? Number(lifecycleMeta.proposed_eta_minutes) : null),
      agreed_eta_at: data.agreed_eta_at ?? lifecycleMeta.agreed_eta_at ?? null,
      eta_confirmed_by_customer_at: data.eta_confirmed_by_customer_at ?? lifecycleMeta.eta_confirmed_by_customer_at ?? null,
      eta_status: data.eta_status ?? lifecycleMeta.eta_status ?? null,
      customer_confirmed_arrival_at: data.customer_confirmed_arrival_at ?? lifecycleMeta.customer_confirmed_arrival_at ?? null,
      overdue_notified_at: data.overdue_notified_at ?? lifecycleMeta.overdue_notified_at ?? null,
      createdAt: data.created_at || data.createdAt || new Date().toISOString(),
      updatedAt: data.updated_at || data.updatedAt || new Date().toISOString(),
    } as Job;
  } catch {
    return null;
  }
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isValidUUID(id: any): boolean {
  if (!id || typeof id !== 'string') return false;
  return UUID_REGEX.test(id.trim());
}

export function toValidUUID(input?: string | null): string {
  if (!input || typeof input !== 'string') {
    return typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : '401bca28-9dca-4f36-af16-8d86088cd14c';
  }
  const clean = input.trim();
  if (isValidUUID(clean)) return clean;
  const mapping: Record<string, string> = {
    'cust-1': '401bca28-9dca-4f36-af16-8d86088cd14c',
    'pro-1': '11111111-1111-4111-8111-111111111111',
    'pro-2': '22222222-2222-4222-8222-222222222222',
    'pro-3': '33333333-3333-4333-8333-333333333333',
    'pro-4': '44444444-4444-4444-8444-444444444444',
    'job-1': '55555555-5555-4555-8555-555555555555',
    'job-2': '66666666-6666-4666-8666-666666666666',
  };
  if (mapping[clean]) return mapping[clean];
  return typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : '401bca28-9dca-4f36-af16-8d86088cd14c';
}

export async function createSupabaseJob(
  job: Partial<Job>,
  mediaFiles?: File[],
  videoFiles?: File[]
): Promise<Job> {
  const generatedId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : toValidUUID(job.id);
  const customerId = toValidUUID(job.customerId);
  let uploadedPhotos: string[] = job.photos || [];
  let uploadedVideos: string[] = job.videos || [];

  if (mediaFiles && mediaFiles.length > 0) {
    const uploadPromises = mediaFiles.map((file) => uploadJobMedia(file, generatedId));
    const uploadedUrls = await Promise.all(uploadPromises);
    uploadedPhotos = [...uploadedPhotos, ...uploadedUrls];
  }

  // FEATURE 2: Upload direct customer device video files to Supabase Storage
  if (videoFiles && videoFiles.length > 0) {
    const videoUploadPromises = videoFiles.map((vFile) => uploadJobVideoFile(vFile, generatedId));
    const uploadedVideoUrls = await Promise.all(videoUploadPromises);
    uploadedVideos = [...uploadedVideos, ...uploadedVideoUrls];
  }

  let resolvedCustomerName = job.customerName || 'Customer';
  let resolvedCustomerPhone = cleanContactPhone(job.customerPhone);

  if ((!resolvedCustomerPhone || resolvedCustomerName === 'Customer') && customerId) {
    try {
      const { data: custProf } = await supabase
        .from('profiles')
        .select('full_name, phone')
        .eq('id', customerId)
        .maybeSingle();
      if (custProf) {
        if (custProf.full_name && resolvedCustomerName === 'Customer') {
          resolvedCustomerName = custProf.full_name;
        }
        if (cleanContactPhone(custProf.phone) && !resolvedCustomerPhone) {
          resolvedCustomerPhone = cleanContactPhone(custProf.phone);
        }
      }
    } catch (_) {}
  }

  // Bundle ai_analysis, customer_meta, location_meta, and direct assignment lifecycle_meta inside ai_scope_estimate
  const bundledScope: any = {
    ...(job.aiScopeEstimate || {}),
    ai_analysis: job.aiAnalysis || null,
    customer_meta: {
      ...((job.aiScopeEstimate as any)?.customer_meta || {}),
      customerName: resolvedCustomerName,
      customerPhone: resolvedCustomerPhone,
    },
    location_meta: {
      cityArea: job.cityArea || '',
      address: job.address || '',
      formattedAddress: job.formattedAddress || job.address || '',
      locationLatitude: job.locationLatitude,
      locationLongitude: job.locationLongitude,
      placeId: job.placeId || '',
      locationLandmark: job.locationLandmark || '',
      locationNotes: job.locationNotes || '',
      locationConfidence: job.locationConfidence || 'manual_confirmed',
      locationConfirmed: job.locationConfirmed ?? true,
      lgaConfirmed: job.lgaConfirmed ?? true,
    },
    ...(job.selectedProfessionalId
      ? {
          lifecycle_meta: {
            selected_professional_id: job.selectedProfessionalId,
            selected_professional_name: job.selectedProfessionalName || '',
            direct_assignment: true,
          },
        }
      : {}),
  };

  const jobPayload: any = {
    id: generatedId,
    customerId,
    customer_id: customerId,
    category_slug: job.categorySlug || 'other',
    category_name: job.categoryName || 'Service',
    title: job.title || 'Service Request',
    description: job.description || '',
    state: job.state || 'Lagos',
    lga: job.lga || 'Ikeja',
    city_area: job.cityArea || '',
    cityArea: job.cityArea || '',
    address: job.address || `${job.lga || 'Ikeja'}, ${job.state || 'Lagos'}`,
    formatted_address: job.formattedAddress || job.address || '',
    formattedAddress: job.formattedAddress || job.address || '',
    location_latitude: job.locationLatitude,
    locationLatitude: job.locationLatitude,
    location_longitude: job.locationLongitude,
    locationLongitude: job.locationLongitude,
    place_id: job.placeId,
    placeId: job.placeId,
    location_landmark: job.locationLandmark || '',
    locationLandmark: job.locationLandmark || '',
    location_notes: job.locationNotes || '',
    locationNotes: job.locationNotes || '',
    location_confirmed: job.locationConfirmed ?? true,
    locationConfirmed: job.locationConfirmed ?? true,
    location_confidence: job.locationConfidence || 'manual_confirmed',
    locationConfidence: job.locationConfidence || 'manual_confirmed',
    lga_confirmed: job.lgaConfirmed ?? true,
    lgaConfirmed: job.lgaConfirmed ?? true,
    urgency: job.urgency || 'within_48h',
    budget_estimate: job.budgetEstimate || 25000,
    status: 'open',
    selectedProfessionalId: job.selectedProfessionalId,
    selected_professional_id: job.selectedProfessionalId,
    selectedProfessionalName: job.selectedProfessionalName,
    ai_scope_estimate: bundledScope,
    quotes_count: 0,
    customerName: resolvedCustomerName,
    customerPhone: resolvedCustomerPhone,
    photos: uploadedPhotos,
    videos: uploadedVideos,
  };

  try {
    const baseInsert: any = {
      id: generatedId,
      customer_id: customerId,
      category_slug: jobPayload.category_slug,
      category_name: jobPayload.category_name,
      title: jobPayload.title,
      description: jobPayload.description,
      state: jobPayload.state,
      lga: jobPayload.lga,
      address: jobPayload.address,
      urgency: jobPayload.urgency,
      budget_estimate: jobPayload.budget_estimate,
      status: 'open',
      ai_scope_estimate: bundledScope,
      quotes_count: 0,
    };

    // Direct single-roundtrip insert with strict schema columns (location_meta is inside ai_scope_estimate)
    const { data, error } = await supabase
      .from('jobs')
      .insert(baseInsert)
      .select()
      .single();

    if (!error && data) {
      const mediaRecords: any[] = [];
      if (uploadedPhotos.length > 0) {
        uploadedPhotos.forEach((url) => {
          mediaRecords.push({
            job_id: data.id,
            media_type: 'photo',
            media_url: url,
          });
        });
      }
      if (uploadedVideos.length > 0) {
        uploadedVideos.forEach((url) => {
          mediaRecords.push({
            job_id: data.id,
            media_type: 'video',
            media_url: url,
          });
        });
      }
      if (mediaRecords.length > 0) {
        await supabase.from('job_media').insert(mediaRecords);
      }
    }

    // Trigger backend authoritative job persistence & multi-channel notification dispatch
    let serverJobRef = `JM-${generatedId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase()}`;
    let serverMatchingStatus = 'matching_in_progress';
    try {
      const apiRes = await fetch('/api/jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(jobPayload),
      });
      const apiJson = await apiRes.json().catch(() => ({}));
      if (apiJson?.jobReference) serverJobRef = apiJson.jobReference;
      if (apiJson?.matchingStatus) serverMatchingStatus = apiJson.matchingStatus;
    } catch (notifErr) {
      console.warn('Backend job persistence and notification dispatch notice:', notifErr);
    }

    return {
      id: generatedId,
      jobReference: serverJobRef,
      job_reference: serverJobRef,
      matchingStatus: serverMatchingStatus,
      customerId,
      customerName: resolvedCustomerName,
      customerPhone: resolvedCustomerPhone,
      categorySlug: job.categorySlug!,
      categoryName: job.categoryName!,
      title: job.title!,
      description: job.description!,
      state: job.state || 'Lagos',
      lga: job.lga || 'Ikeja',
      cityArea: job.cityArea || '',
      address: job.address || '',
      formattedAddress: job.formattedAddress || job.address || '',
      locationLatitude: job.locationLatitude,
      locationLongitude: job.locationLongitude,
      placeId: job.placeId,
      locationConfirmed: job.locationConfirmed ?? true,
      locationConfidence: job.locationConfidence || 'manual_confirmed',
      lgaConfirmed: job.lgaConfirmed ?? true,
      urgency: job.urgency || 'within_48h',
      budgetEstimate: job.budgetEstimate || 25000,
      status: 'open',
      selectedProfessionalId: job.selectedProfessionalId,
      selectedProfessionalName: job.selectedProfessionalName,
      photos: uploadedPhotos,
      videos: uploadedVideos,
      aiAnalysis: job.aiAnalysis,
      aiScopeEstimate: job.aiScopeEstimate,
      quotesCount: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  } catch (e) {
    console.warn('Jobs table insert note:', e);
  }

  const fallbackJobRef = `JM-${generatedId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase()}`;
  return {
    id: generatedId,
    jobReference: fallbackJobRef,
    job_reference: fallbackJobRef,
    customerId,
    customerName: resolvedCustomerName,
    customerPhone: resolvedCustomerPhone,
    categorySlug: job.categorySlug!,
    categoryName: job.categoryName!,
    title: job.title!,
    description: job.description!,
    state: job.state || 'Lagos',
    lga: job.lga || 'Ikeja',
    cityArea: job.cityArea || '',
    address: job.address || '',
    formattedAddress: job.formattedAddress || job.address || '',
    locationLatitude: job.locationLatitude,
    locationLongitude: job.locationLongitude,
    placeId: job.placeId,
    locationConfirmed: job.locationConfirmed ?? true,
    locationConfidence: job.locationConfidence || 'manual_confirmed',
    lgaConfirmed: job.lgaConfirmed ?? true,
    urgency: job.urgency || 'within_48h',
    budgetEstimate: job.budgetEstimate || 25000,
    status: 'open',
    selectedProfessionalId: job.selectedProfessionalId,
    selectedProfessionalName: job.selectedProfessionalName,
    photos: uploadedPhotos,
    videos: uploadedVideos,
    aiAnalysis: job.aiAnalysis,
    aiScopeEstimate: job.aiScopeEstimate,
    quotesCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

export async function submitSupabaseQuote(quote: Partial<Quote>): Promise<Quote> {
  // Authoritative authenticated user verification
  const { data: authData } = await supabase.auth.getUser();
  const authUser = authData?.user;
  const professionalId = authUser?.id || toValidUUID(quote.professionalId);
  const generatedId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : toValidUUID(quote.id);
  const jobId = toValidUUID(quote.jobId);

  const resolvedArrivalTime =
    quote.estimatedArrivalTime || quote.startDate || 'Tomorrow morning';

  const payload = {
    id: generatedId,
    job_id: jobId,
    professional_id: professionalId,
    labor_cost: quote.laborCost || 0,
    materials_cost: quote.materialsCost || 0,
    total_amount: quote.totalAmount || 0,
    duration_estimate: quote.durationEstimate || '1 Day',
    start_date: resolvedArrivalTime,
    warranty_days: quote.warrantyDays || 30,
    notes: quote.notes || '',
    status: 'submitted',
  };

  // 1. Insert directly via authenticated browser client so RLS (auth.uid() = professional_id) succeeds immediately
  try {
    await supabase.from('quotes').upsert(payload, { onConflict: 'id' });
  } catch (_) {}

  try {
    // 2. Authoritative backend API validation, verification enforcement & notification dispatch
    const authHeaders = await getAuthHeaders();
    const srvRes = await fetch('/api/quotes', {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        ...payload,
        jobId,
        professionalId,
        estimatedArrivalTime: resolvedArrivalTime,
        professionalName: quote.professionalName,
        professionalEmail: (quote as any).professionalEmail,
        professionalPhone: (quote as any).professionalPhone,
      }),
    });

    const srvData = await srvRes.json().catch(() => ({}));
    if (!srvRes.ok || !srvData.success) {
      throw new Error(srvData.error || 'Failed to submit quote. Please verify the job is open and try again.');
    }

    const finalArrival =
      srvData.quote?.estimated_arrival_time ||
      srvData.quote?.estimatedArrivalTime ||
      srvData.quote?.start_date ||
      payload.start_date;

    const resolvedQuoteId = srvData.quote?.id || payload.id;
    const resolvedQuoteRef =
      srvData.quoteReference ||
      srvData.quote?.quote_reference ||
      srvData.quote?.quoteReference ||
      `QT-${String(resolvedQuoteId).replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase()}`;

    return {
      id: resolvedQuoteId,
      quoteReference: resolvedQuoteRef,
      quote_reference: resolvedQuoteRef,
      jobId,
      professionalId,
      professionalName: quote.professionalName || 'Artisan',
      professionalAvatar: quote.professionalAvatar || '',
      professionalBusiness: quote.professionalBusiness,
      professionalCategory: quote.professionalCategory || 'plumber',
      professionalRating: quote.professionalRating || 5.0,
      professionalReviewCount: quote.professionalReviewCount || 0,
      professionalVerification: quote.professionalVerification || 'verified',
      professionalYearsExp: quote.professionalYearsExp || 5,
      laborCost: Number(srvData.quote?.labor_cost || payload.labor_cost),
      materialsCost: Number(srvData.quote?.materials_cost || payload.materials_cost),
      totalAmount: Number(srvData.quote?.total_amount || payload.total_amount),
      durationEstimate: srvData.quote?.duration_estimate || payload.duration_estimate,
      startDate: finalArrival,
      estimatedArrivalTime: finalArrival,
      warrantyDays: Number(srvData.quote?.warranty_days || payload.warranty_days),
      notes: srvData.quote?.notes || payload.notes,
      status: srvData.quote?.status || 'submitted',
      createdAt: srvData.quote?.created_at || new Date().toISOString(),
    };
  } catch (err: any) {
    console.error('Quote submission error in submitSupabaseQuote:', err);
    throw err;
  }
}

export async function getSupabaseQuotes(jobId?: string): Promise<Quote[] | null> {
  try {
    let query = supabase
      .from('quotes')
      .select('*, profiles:professional_id(*)');

    if (jobId) {
      query = query.eq('job_id', jobId);
    }

    const [dbRes, apiRes] = await Promise.all([
      query,
      fetch(jobId ? `/api/quotes?jobId=${encodeURIComponent(jobId)}` : '/api/quotes')
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
    ]);

    const mapQuoteRow = (row: any): Quote => {
      const pro = row.profiles || {};
      const arrivalTime =
        row.estimated_arrival_time ||
        row.estimatedArrivalTime ||
        row.start_date ||
        row.startDate ||
        'Tomorrow morning';
      const qRef =
        row.quote_reference ||
        row.quoteReference ||
        `QT-${String(row.id || '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase()}`;
      return {
        id: row.id,
        quoteReference: qRef,
        quote_reference: qRef,
        jobId: row.job_id || row.jobId,
        professionalId: row.professional_id || row.professionalId,
        professionalName: row.professional_name || row.professionalName || pro.full_name || 'Artisan',
        professionalPhone: cleanContactPhone(row.professional_phone || row.professionalPhone || pro.phone) || undefined,
        professionalAvatar: row.professional_avatar || row.professionalAvatar || pro.avatar_url || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
        professionalBusiness: row.professional_business || row.professionalBusiness || pro.business_name || `${pro.full_name || row.professional_name || 'Artisan'} Services`,
        professionalCategory: row.professional_category || row.professionalCategory || pro.primary_category || 'plumber',
        professionalRating: Number(row.professional_rating || row.professionalRating || pro.rating || 5.0),
        professionalReviewCount: Number(row.professional_review_count || row.professionalReviewCount || pro.review_count || 0),
        professionalVerification: row.professional_verification || row.professionalVerification || pro.verification_status || 'verified',
        professionalYearsExp: Number(row.professional_years_exp || row.professionalYearsExp || pro.years_experience || 5),
        laborCost: Number(row.labor_cost ?? row.laborCost) || 0,
        materialsCost: Number(row.materials_cost ?? row.materialsCost) || 0,
        totalAmount: Number(row.total_amount ?? row.totalAmount) || 0,
        durationEstimate: row.duration_estimate || row.durationEstimate || '1 Day',
        startDate: arrivalTime,
        estimatedArrivalTime: arrivalTime,
        warrantyDays: Number(row.warranty_days ?? row.warrantyDays) || 30,
        notes: row.notes || '',
        status: row.status || 'submitted',
        createdAt: row.created_at || row.createdAt || new Date().toISOString(),
      };
    };

    const mergedMap = new Map<string, Quote>();
    if (apiRes?.success && Array.isArray(apiRes.quotes)) {
      apiRes.quotes.forEach((r: any) => {
        if (r?.id) mergedMap.set(r.id, mapQuoteRow(r));
      });
    }
    if (!dbRes.error && Array.isArray(dbRes.data)) {
      dbRes.data.forEach((r: any) => {
        if (r?.id) mergedMap.set(r.id, mapQuoteRow(r));
      });
    }

    if (mergedMap.size === 0 && dbRes.error && !apiRes?.success) return null;
    return Array.from(mergedMap.values());
  } catch {
    return null;
  }
}

export async function rejectSupabaseQuote(
  quoteId: string,
  jobId: string,
  customerId: string,
  reason?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const authHeaders = await getAuthHeaders();
    const res = await fetch(`/api/quotes/${encodeURIComponent(quoteId)}/reject`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({ quoteId, jobId, customerId, reason }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      return { success: false, error: data.error || 'Failed to reject quotation.' };
    }
    try {
      await supabase.from('quotes').update({ status: 'declined' }).eq('id', quoteId);
    } catch (_) {}
    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error rejecting quotation.' };
  }
}

export async function acceptSupabaseQuote(
  quoteId: string,
  jobId: string,
  professionalId: string,
  customerId: string,
  options?: { alreadyPaid?: boolean; paymentReference?: string }
) {
  try {
    // 0. Call authoritative backend quote-acceptance endpoint first
    try {
      const authHeaders = await getAuthHeaders();
      await fetch(`/api/quotes/${encodeURIComponent(quoteId)}/accept`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ quoteId, jobId, professionalId, customerId, ...options }),
      });
    } catch (_) {}

    // 1. Update quote status to accepted
    await supabase.from('quotes').update({ status: 'accepted' }).eq('id', quoteId);
    await supabase.from('quotes').update({ status: 'declined' }).eq('job_id', jobId).neq('id', quoteId);

    // 2. Update job and lifecycle_meta (preserve paid/in_progress state if already paid!)
    const { data: existingJob } = await supabase.from('jobs').select('status, ai_scope_estimate').eq('id', jobId).maybeSingle();
    const prevScope = existingJob?.ai_scope_estimate || {};
    const prevMeta = prevScope.lifecycle_meta || {};
    const isAlreadyFunded = Boolean(
      options?.alreadyPaid ||
        prevMeta.payment_status === 'held_in_escrow' ||
        prevMeta.payment_status === 'paid' ||
        prevMeta.payment_status === 'released' ||
        existingJob?.status === 'in_progress' ||
        existingJob?.status === 'completed'
    );
    const nowIso = new Date().toISOString();
    const etaDeadlineIso = prevMeta.eta_agreement_deadline_at || new Date(Date.now() + 60 * 60000).toISOString();
    await supabase.from('jobs').update({
      status: isAlreadyFunded ? (existingJob?.status === 'completed' ? 'completed' : 'in_progress') : 'assigned',
      selected_professional_id: professionalId,
      ai_scope_estimate: {
        ...prevScope,
        lifecycle_meta: {
          ...prevMeta,
          stage: isAlreadyFunded ? Math.max(Number(prevMeta.stage || 2), 2) : 1,
          status: isAlreadyFunded ? (prevMeta.status === 'awaiting_confirmation' ? 'awaiting_confirmation' : 'in_progress') : 'awaiting_payment',
          payment_status: isAlreadyFunded ? (prevMeta.payment_status === 'released' ? 'released' : 'held_in_escrow') : 'awaiting_payment',
          payment_reference: options?.paymentReference || prevMeta.payment_reference,
          selected_professional_id: professionalId,
          accepted_quote_id: quoteId,
          accepted_at: prevMeta.accepted_at || nowIso,
          assigned_at: prevMeta.assigned_at || nowIso,
          eta_agreement_deadline_at: etaDeadlineIso,
          eta_status: prevMeta.eta_status || 'awaiting_proposal',
          grace_period_minutes: prevMeta.grace_period_minutes ?? 15,
        },
      },
      updated_at: nowIso,
    }).eq('id', jobId);

    // 3. Create job assignment
    await supabase.from('job_assignments').upsert(
      {
        job_id: jobId,
        quote_id: quoteId,
        customer_id: customerId,
        professional_id: professionalId,
        status: isAlreadyFunded ? 'in_progress' : 'assigned',
      },
      { onConflict: 'job_id' }
    );

    // 4. Create conversation thread
    await supabase.from('conversations').insert({
      job_id: jobId,
      customer_id: customerId,
      professional_id: professionalId,
    });

    if (!isAlreadyFunded) {
      // 5. Send notification to professional
      await supabase.from('notifications').insert({
        user_id: professionalId,
        title: 'Congratulations! Your Quote was Accepted',
        message: 'The customer has accepted your quote. Please wait for escrow payment confirmation before starting work.',
        type: 'quote_accepted',
        target_view: 'pro_dashboard',
        target_job_id: jobId,
      });

      // 6. Multi-channel Email & SMS notification to customer and professional
      try {
        await fetch('/api/notifications/customer-lifecycle-event', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            eventType: 'PROFESSIONAL_SELECTED',
            jobId,
            professional: { id: professionalId },
            quote: { id: quoteId },
            customer: { id: customerId },
          }),
        });
      } catch (_) {}
    }
  } catch (err) {
    console.warn('Accept quote error:', err);
  }
}

export async function getEligibleChatContacts(userId: string): Promise<EligibleChatContact[]> {
  if (!userId) return [];
  try {
    const headers = await getAuthHeaders({ 'x-user-id': userId });
    const res = await fetch(`/api/messages/contacts?userId=${encodeURIComponent(userId)}`, {
      headers,
    });
    if (res.ok) {
      const json = await res.json();
      if (json.success && Array.isArray(json.contacts)) {
        return json.contacts;
      }
    }
  } catch (err) {
    console.warn('getEligibleChatContacts notice:', err);
  }
  return [];
}

export async function getSupabaseMessages(
  jobId?: string | null,
  userId?: string,
  options?: { conversationId?: string; withUserId?: string }
): Promise<Message[]> {
  try {
    const params = new URLSearchParams();
    if (jobId) params.append('jobId', jobId);
    if (userId) params.append('userId', userId);
    if (options?.conversationId) params.append('conversationId', options.conversationId);
    if (options?.withUserId) params.append('withUserId', options.withUserId);

    // 1. Fetch via authoritative backend proxy
    try {
      const headers = await getAuthHeaders(userId ? { 'x-user-id': userId } : {});
      const res = await fetch(`/api/messages?${params.toString()}`, { headers });
      if (res.ok) {
        const json = await res.json();
        if (json.success && Array.isArray(json.messages)) {
          return json.messages;
        }
      }
    } catch (apiErr) {
      console.warn('API /api/messages fetch notice:', apiErr);
    }

    // 2. Direct Supabase fallback
    const rawJobId = jobId ? String(jobId).trim() : '';
    const isDirect = !rawJobId || rawJobId.startsWith('direct:');
    const safeJobId = !isDirect ? toValidUUID(rawJobId) : null;
    const safeUserId = userId && isValidUUID(userId) ? userId : null;

    let query = supabase
      .from('messages')
      .select('*, sender:sender_id(id, full_name, role, avatar_url), receiver:receiver_id(id, full_name, role, avatar_url)')
      .order('created_at', { ascending: true });

    if (options?.conversationId && isValidUUID(options.conversationId)) {
      query = query.eq('conversation_id', options.conversationId);
    } else if (safeJobId) {
      query = query.eq('job_id', safeJobId);
    } else if (safeUserId) {
      query = query.or(`sender_id.eq.${safeUserId},receiver_id.eq.${safeUserId}`);
    } else {
      return [];
    }

    const { data, error } = await query;
    if (error || !data) return [];

    return data.map((row: any) => {
      const sender = row.sender || {};
      const receiver = row.receiver || {};

      return {
        id: row.id,
        jobId: row.job_id || null,
        conversationId: row.conversation_id || undefined,
        senderId: row.sender_id,
        senderName: sender.full_name || 'Artisan / Customer',
        senderRole: sender.role || 'customer',
        senderAvatar: sender.avatar_url || '',
        receiverId: row.receiver_id,
        receiverName: receiver.full_name,
        receiverRole: receiver.role,
        receiverAvatar: receiver.avatar_url,
        content: row.content,
        attachments: Array.isArray(row.attachments) ? row.attachments : [],
        read: Boolean(row.read ?? row.is_read),
        delivered: true,
        readAt: row.read_at || undefined,
        createdAt: row.created_at,
      };
    });
  } catch (err) {
    console.warn('getSupabaseMessages exception:', err);
    return [];
  }
}

export async function sendSupabaseMessage(payload: {
  jobId?: string | null;
  conversationId?: string;
  senderId: string;
  receiverId: string;
  content: string;
  attachments?: string[];
  senderName?: string;
  senderRole?: UserRole;
  senderAvatar?: string;
}): Promise<Message> {
  const rawJobId = payload.jobId ? String(payload.jobId).trim() : '';
  const isDirect = !rawJobId || rawJobId === 'direct' || rawJobId.startsWith('direct:');
  const safeJobId = !isDirect ? (isValidUUID(rawJobId) ? rawJobId : toValidUUID(rawJobId)) : null;
  const safeSenderId = isValidUUID(payload.senderId) ? payload.senderId : toValidUUID(payload.senderId);
  const safeReceiverId = isValidUUID(payload.receiverId) ? payload.receiverId : toValidUUID(payload.receiverId);
  const cleanContent = (payload.content || '').trim();
  const cleanAttachments = Array.isArray(payload.attachments)
    ? payload.attachments.filter((a) => typeof a === 'string' && a.trim().length > 0)
    : [];

  // 1. Authoritative Backend Proxy Dispatch
  try {
    const headers = await getAuthHeaders({
      'Content-Type': 'application/json',
      'x-user-id': safeSenderId,
    });
    const res = await fetch('/api/messages', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        jobId: safeJobId,
        conversationId: payload.conversationId,
        senderId: safeSenderId,
        receiverId: safeReceiverId,
        content: cleanContent,
        attachments: cleanAttachments,
        senderName: payload.senderName,
        senderRole: payload.senderRole,
        senderAvatar: payload.senderAvatar,
      }),
    });

    const data = await res.json().catch(() => ({}));
    if (res.ok && data.success && data.message) {
      return data.message;
    }
    if (!res.ok && data.error) {
      throw new Error(data.error);
    }
  } catch (apiErr: any) {
    if (apiErr?.message && (apiErr.message.includes('Forbidden') || apiErr.message.includes('Cannot message'))) {
      throw apiErr;
    }
    console.warn('Backend message API dispatch notice:', apiErr);
  }

  // 2. Direct Supabase insert fallback
  const generatedId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : toValidUUID(`msg-${Date.now()}`);
  const nowIso = new Date().toISOString();
  const displayContent = cleanContent || (cleanAttachments.length > 0 ? '📎 Sent an attachment' : '');

  const messageRecord: Record<string, any> = {
    id: generatedId,
    job_id: safeJobId,
    sender_id: safeSenderId,
    receiver_id: safeReceiverId,
    content: displayContent,
    attachments: cleanAttachments,
    read: false,
    created_at: nowIso,
  };

  try {
    await supabase.from('messages').insert(messageRecord);
  } catch (err) {
    console.warn('Direct message insert fallback note:', err);
  }

  return {
    id: generatedId,
    jobId: safeJobId,
    conversationId: payload.conversationId,
    senderId: safeSenderId,
    senderName: payload.senderName || 'You',
    senderRole: payload.senderRole || 'customer',
    senderAvatar: payload.senderAvatar || '',
    receiverId: safeReceiverId,
    content: displayContent,
    attachments: cleanAttachments,
    read: false,
    delivered: true,
    createdAt: nowIso,
  };
}

export async function markSupabaseMessagesRead(
  jobId: string | null | undefined,
  userId: string,
  options?: { conversationId?: string; senderId?: string }
): Promise<void> {
  try {
    const rawJobId = jobId ? String(jobId).trim() : '';
    const isDirect = !rawJobId || rawJobId.startsWith('direct:');
    const safeJobId = !isDirect ? (isValidUUID(rawJobId) ? rawJobId : toValidUUID(rawJobId)) : null;
    const directSenderId = rawJobId.startsWith('direct:')
      ? rawJobId.replace('direct:', '').trim()
      : options?.senderId;
    const safeUserId = isValidUUID(userId) ? userId : toValidUUID(userId);

    const headers = await getAuthHeaders({
      'Content-Type': 'application/json',
      'x-user-id': safeUserId,
    });

    // Call backend API
    fetch('/api/messages/mark-read', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        jobId: safeJobId,
        conversationId: options?.conversationId,
        senderId: directSenderId,
        userId: safeUserId,
      }),
    }).catch(() => {});

    // Direct Supabase update
    if (safeJobId) {
      await supabase
        .from('messages')
        .update({ read: true })
        .eq('job_id', safeJobId)
        .eq('receiver_id', safeUserId);
    } else if (options?.conversationId && isValidUUID(options.conversationId)) {
      await supabase
        .from('messages')
        .update({ read: true })
        .eq('conversation_id', options.conversationId)
        .eq('receiver_id', safeUserId);
    } else if (directSenderId && isValidUUID(directSenderId)) {
      await supabase
        .from('messages')
        .update({ read: true })
        .eq('sender_id', directSenderId)
        .eq('receiver_id', safeUserId)
        .is('job_id', null);
    }
  } catch (_) {}
}

// ============================================================================
// MARKETPLACE COMMISSION, ESCROW & PAYSTACK HELPERS
// ============================================================================

export async function fetchMarketplaceConfig(): Promise<{
  success: boolean;
  isConfigured: boolean;
  publicKey: string;
  currency: string;
  commissionPercentage: number;
  minCommissionPercentage: number;
  maxCommissionPercentage: number;
}> {
  try {
    const res = await fetch('/api/marketplace/config');
    if (res.ok) {
      return await res.json();
    }
  } catch (err) {
    console.info('Marketplace config fetch notice:', err);
  }

  return {
    success: true,
    isConfigured: false,
    publicKey: '',
    currency: 'NGN',
    commissionPercentage: 10,
    minCommissionPercentage: 0,
    maxCommissionPercentage: 30,
  };
}

export async function initializeMarketplacePayment(payload: {
  jobId: string;
  quoteId?: string;
  customerId: string;
  professionalId: string;
  grossAmount: number;
  customerEmail: string;
  customerName?: string;
  callbackUrl?: string;
}): Promise<{
  success: boolean;
  alreadyPaid?: boolean;
  authorization_url?: string;
  access_code?: string;
  reference?: string;
  transaction?: MarketplaceTransaction;
  error?: string;
}> {
  try {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/marketplace/paystack/initialize', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    return await res.json();
  } catch (err: any) {
    return {
      success: false,
      error: err.message || 'Network error initializing Paystack marketplace payment.',
    };
  }
}

export async function verifyMarketplacePayment(reference: string): Promise<{
  success: boolean;
  alreadyProcessed?: boolean;
  message?: string;
  transaction?: MarketplaceTransaction;
  job?: any;
  error?: string;
}> {
  try {
    const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const res = await fetch('/api/marketplace/paystack/verify', {
      method: 'POST',
      headers,
      body: JSON.stringify({ reference }),
    });

    const data = await res.json();

    // Ensure authenticated client session also persists in_progress + held_in_escrow on the job row
    if (data?.success && data?.transaction?.jobId && isSupabaseConfigured) {
      try {
        const tx = data.transaction;
        const safeJobId = toValidUUID(tx.jobId);
        const nowIso = new Date().toISOString();
        const { data: existingJob } = await supabase
          .from('jobs')
          .select('status, selected_professional_id, ai_scope_estimate')
          .eq('id', safeJobId)
          .maybeSingle();

        const prevScope = existingJob?.ai_scope_estimate || {};
        const prevMeta = prevScope.lifecycle_meta || {};
        const isAlreadyCompleted =
          existingJob?.status === 'completed' ||
          prevMeta.status === 'completed' ||
          prevMeta.payment_status === 'released' ||
          prevMeta.payment_released === true;

        if (!isAlreadyCompleted) {
          const isProDone =
            existingJob?.status === 'completed_by_professional' ||
            prevMeta.status === 'completed_by_professional' ||
            prevMeta.completed_by_professional === true;
          await supabase
            .from('jobs')
            .update({
              status: 'in_progress',
              selected_professional_id: tx.professionalId || existingJob?.selected_professional_id,
              ai_scope_estimate: {
                ...prevScope,
                lifecycle_meta: {
                  ...prevMeta,
                  stage: isProDone ? 3 : Math.max(Number(prevMeta.stage || 2), 2),
                  status: isProDone ? 'completed_by_professional' : 'in_progress',
                  payment_status: 'held_in_escrow',
                  escrow_held: true,
                  payment_reference: reference || tx.paymentReference,
                  transaction_id: tx.id,
                  gross_amount: tx.grossAmount,
                  selected_professional_id: tx.professionalId || prevMeta.selected_professional_id,
                  accepted_quote_id: tx.quoteId || prevMeta.accepted_quote_id,
                  hired_at: prevMeta.hired_at || nowIso,
                },
              },
              updated_at: nowIso,
            })
            .eq('id', safeJobId);
        }
      } catch (_) {}
    }

    return data;
  } catch (err: any) {
    return {
      success: false,
      error: err.message || 'Network error verifying marketplace payment.',
    };
  }
}

export async function checkJobPaymentStatus(jobId: string): Promise<{
  success: boolean;
  isPaid: boolean;
  paymentStatus: string;
  canRetry: boolean;
  transaction?: any;
}> {
  try {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/marketplace/jobs/${encodeURIComponent(jobId)}/payment-status`, {
      headers,
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err) {
    console.warn('Payment status check notice:', err);
  }
  return { success: false, isPaid: false, paymentStatus: 'unknown', canRetry: true };
}

export async function fetchProfessionalEarnings(professionalId: string): Promise<{
  success: boolean;
  earnings: ProfessionalEarningsSummary;
}> {
  try {
    const headers = await getAuthHeaders({ 'x-user-id': professionalId });
    const res = await fetch(`/api/marketplace/professionals/${encodeURIComponent(professionalId)}/earnings`, {
      headers,
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err) {
    console.info('Professional earnings fetch note:', err);
  }

  return {
    success: true,
    earnings: {
      totalJobsCompleted: 0,
      totalGrossJobValue: 0,
      totalJobMatchCommission: 0,
      totalProfessionalEarnings: 0,
      totalProfessionalEarningsKobo: 0,
      pendingEarnings: 0,
      pendingEarningsKobo: 0,
      availableEarnings: 0,
      availableEarningsKobo: 0,
      paidOutEarnings: 0,
      paidOutEarningsKobo: 0,
      processingPayouts: 0,
      processingPayoutsKobo: 0,
      successfulTransactionsCount: 0,
      recentTransactions: [],
      payoutHistory: [],
      bankAccount: null,
    },
  };
}


/**
 * Authoritative Supabase RPC invocation: release_job_payment
 * Validates authenticated customer, verifies job state is professional_completed,
 * prevents double payments, writes transaction & ledger records, and atomically credits artisan balance.
 */
export async function releaseJobPaymentRpc(
  jobId: string,
  notes?: string,
  callerCustomerId?: string
): Promise<{
  success: boolean;
  jobId?: string;
  releasedAmount?: number;
  grossAmount?: number;
  commissionAmount?: number;
  professionalId?: string;
  customerId?: string;
  paymentReference?: string;
  message?: string;
  transaction?: MarketplaceTransaction;
  error?: string;
}> {
  try {
    const { data, error } = await supabase.rpc('release_job_payment', {
      target_job_id: jobId,
      notes: notes || null,
    });

    if (!error && data && data.success) {
      return {
        success: true,
        jobId: data.job_id || jobId,
        releasedAmount: data.released_amount,
        grossAmount: data.gross_amount,
        commissionAmount: data.commission_amount,
        professionalId: data.professional_id,
        customerId: data.customer_id,
        paymentReference: data.payment_reference,
        message: data.message || 'Job payment successfully released.',
      };
    }
  } catch (rpcErr) {
    console.info('Direct Supabase RPC release_job_payment attempt note:', rpcErr);
  }

  // Gracefully fall back to backend endpoint with full transactional resilience
  let customerId = callerCustomerId || '';
  if (!customerId) {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      customerId = user?.id || '';
    } catch (_) {}
  }
  return await confirmJobWorkCompletionApi(jobId, customerId, notes);
}

export async function confirmJobWorkCompletionApi(jobId: string, customerId: string, notes?: string): Promise<{
  success: boolean;
  releasedAmount?: number;
  message?: string;
  transaction?: MarketplaceTransaction;
  error?: string;
}> {
  try {
    const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const res = await fetch(`/api/marketplace/jobs/${jobId}/confirm-completion`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ customerId, notes }),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error confirming job completion.' };
  }
}

export async function submitJobReviewApi(payload: {
  jobId: string;
  rating: number;
  comment?: string;
  customerId?: string;
  professionalId?: string;
  qualityRating?: number;
  punctualityRating?: number;
  valueRating?: number;
}): Promise<{
  success: boolean;
  message?: string;
  reviewId?: string;
  averageRating?: number;
  reviewCount?: number;
  error?: string;
}> {
  try {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/reviews', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error submitting review.' };
  }
}

export async function fetchProfessionalReviewsApi(proId: string): Promise<{
  success: boolean;
  reviews: any[];
  averageRating: number;
  totalReviews: number;
  breakdown: Record<number, number>;
}> {
  try {
    const res = await fetch(`/api/professionals/${proId}/reviews`);
    if (res.ok) {
      return await res.json();
    }
  } catch (err) {
    console.warn('Fetch pro reviews notice:', err);
  }
  return {
    success: false,
    reviews: [],
    averageRating: 5.0,
    totalReviews: 0,
    breakdown: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
  };
}

export async function fetchJobReviewApi(jobId: string): Promise<{
  success: boolean;
  review: any | null;
}> {
  try {
    const res = await fetch(`/api/jobs/${jobId}/review`);
    if (res.ok) {
      return await res.json();
    }
  } catch (err) {
    console.warn('Fetch job review notice:', err);
  }
  return { success: false, review: null };
}

export async function professionalMarkJobCompleteApi(
  jobId: string,
  professionalId: string,
  completionPhotoUrl?: string,
  completionNotes?: string
): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  try {
    const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const res = await fetch(`/api/marketplace/jobs/${jobId}/mark-completed`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ professionalId, completionPhotoUrl, completionNotes }),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error marking job completed.' };
  }
}

export async function fetchNigerianBanksApi(): Promise<{
  success: boolean;
  banks: NigerianBankOption[];
  source?: string;
}> {
  try {
    const res = await fetch('/api/paystack/banks');
    if (res.ok) {
      return await res.json();
    }
  } catch (err) {
    console.warn('Fetch Nigerian banks notice:', err);
  }
  return {
    success: true,
    banks: [
      { id: 1, name: 'Access Bank', code: '044' },
      { id: 2, name: 'Guaranty Trust Bank (GTBank)', code: '058' },
      { id: 3, name: 'United Bank For Africa (UBA)', code: '033' },
      { id: 4, name: 'Zenith Bank', code: '057' },
      { id: 5, name: 'First Bank of Nigeria', code: '011' },
      { id: 6, name: 'Kuda Bank', code: '50211' },
      { id: 7, name: 'Moniepoint MFB', code: '50515' },
      { id: 8, name: 'OPay Digital Services (OPay)', code: '999992' },
      { id: 9, name: 'PalmPay', code: '999991' },
      { id: 10, name: 'Fidelity Bank', code: '070' },
      { id: 11, name: 'First City Monument Bank (FCMB)', code: '214' },
      { id: 12, name: 'Stanbic IBTC Bank', code: '221' },
      { id: 13, name: 'Sterling Bank', code: '232' },
      { id: 14, name: 'Union Bank of Nigeria', code: '032' },
      { id: 15, name: 'Wema Bank', code: '035' },
    ],
  };
}

export async function fetchProfessionalBankAccountApi(professionalId: string): Promise<{
  success: boolean;
  bankAccount: ProfessionalBankAccount | null;
  error?: string;
}> {
  try {
    const headers = await getAuthHeaders({ 'x-user-id': professionalId });
    const res = await fetch(`/api/marketplace/professionals/${encodeURIComponent(professionalId)}/bank-account`, {
      headers,
    });
    if (res.ok) {
      return await res.json();
    }
  } catch (err: any) {
    console.warn('Fetch professional bank account notice:', err);
  }
  return { success: false, bankAccount: null };
}

export async function resolveProfessionalBankAccountApi(payload: {
  professionalId: string;
  accountNumber: string;
  bankCode: string;
}): Promise<{
  success: boolean;
  accountNumber?: string;
  accountName?: string;
  bankCode?: string;
  error?: string;
}> {
  try {
    const headers = await getAuthHeaders({
      'Content-Type': 'application/json',
      'x-user-id': payload.professionalId,
    });
    const res = await fetch('/api/professionals/me/bank-account/resolve', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error resolving bank account.' };
  }
}

export async function saveProfessionalBankAccountApi(payload: {
  professionalId: string;
  bankCode: string;
  bankName?: string;
  accountNumber: string;
}): Promise<{
  success: boolean;
  message?: string;
  bankAccount?: ProfessionalBankAccount;
  error?: string;
}> {
  try {
    const headers = await getAuthHeaders({
      'Content-Type': 'application/json',
      'x-user-id': payload.professionalId,
    });
    const res = await fetch(`/api/marketplace/professionals/${encodeURIComponent(payload.professionalId)}/bank-account`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error saving bank account.' };
  }
}

export async function requestProfessionalPayoutApi(payload: {
  professionalId: string;
  amount: number;
  bankName?: string;
  accountNumber?: string;
  accountName?: string;
  bankCode?: string;
  idempotencyKey?: string;
}): Promise<{
  success: boolean;
  alreadyProcessed?: boolean;
  uncertain?: boolean;
  message?: string;
  payout?: ProfessionalPayout;
  error?: string;
}> {
  try {
    const idemKey =
      payload.idempotencyKey ||
      `wd_${payload.professionalId}_${Math.round(Number(payload.amount || 0) * 100)}_${Date.now()}`;
    const headers = await getAuthHeaders({
      'Content-Type': 'application/json',
      'x-user-id': payload.professionalId,
      'x-idempotency-key': idemKey,
    });
    const res = await fetch(`/api/marketplace/professionals/${encodeURIComponent(payload.professionalId)}/payout-request`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ...payload,
        idempotencyKey: idemKey,
      }),
    });

    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error requesting payout.' };
  }
}

export async function reconcileProfessionalPayoutApi(payload: {
  professionalId: string;
  reference: string;
}): Promise<{
  success: boolean;
  reconciledStatus?: string;
  payout?: ProfessionalPayout;
  error?: string;
}> {
  try {
    const headers = await getAuthHeaders({
      'Content-Type': 'application/json',
      'x-user-id': payload.professionalId,
    });
    const res = await fetch(`/api/professionals/me/payouts/${encodeURIComponent(payload.reference)}/reconcile`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        professionalId: payload.professionalId,
        reference: payload.reference,
      }),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error reconciling transfer status.' };
  }
}

export async function fetchAdminFinancialsOverview(): Promise<{
  success: boolean;
  financials: AdminPlatformRevenue;
}> {
  try {
    const headers = await getAdminHeaders();
    const res = await fetch('/api/admin/financials-overview', { headers });
    if (res.ok) {
      return await res.json();
    }
  } catch (err) {
    console.info('Admin financials overview fetch note:', err);
  }

  return {
    success: true,
    financials: {
      totalCustomerPayments: 0,
      totalJobMatchCommissions: 0,
      totalProfessionalEarnings: 0,
      totalPayouts: 0,
      pendingPayouts: 0,
      refundedTransactions: 0,
      failedPayments: 0,
      successfulTransactionsCount: 0,
      currentCommissionPercentage: 10,
      recentTransactions: [],
      pendingPayoutsList: [],
      auditLogs: [],
    },
  };
}

export async function updateAdminCommissionRate(
  percentage: number,
  reason?: string
): Promise<{ success: boolean; percentage?: number; message?: string; error?: string }> {
  try {
    const headers = await getAdminHeaders({ 'Content-Type': 'application/json' });
    const res = await fetch('/api/admin/commission/update', {
      method: 'POST',
      headers,
      body: JSON.stringify({ percentage, reason }),
    });

    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message || 'Error updating commission rate.' };
  }
}

export async function processAdminPayoutActionApi(
  payoutId: string,
  action: 'approve' | 'mark_paid' | 'cancel',
  notes?: string
): Promise<{ success: boolean; payout?: ProfessionalPayout; message?: string; error?: string }> {
  try {
    const headers = await getAdminHeaders({ 'Content-Type': 'application/json' });
    const res = await fetch(`/api/admin/payouts/${payoutId}/process`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ action, notes }),
    });

    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message || 'Error processing payout action.' };
  }
}

export async function processAdminRefundApi(
  transactionId: string,
  reason: string
): Promise<{ success: boolean; transaction?: MarketplaceTransaction; message?: string; error?: string }> {
  try {
    const headers = await getAdminHeaders({ 'Content-Type': 'application/json' });
    const res = await fetch(`/api/admin/transactions/${transactionId}/refund`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ reason }),
    });

    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message || 'Error processing refund.' };
  }
}

// Backward-Compatible Professional Subscription Helper
export async function fetchProfessionalSubscription(professionalId: string): Promise<{
  subscription: ProfessionalSubscription | null;
  status: SubscriptionStatus;
  isEligible: boolean;
  payments: SubscriptionPayment[];
}> {
  return {
    subscription: {
      id: `mkt-${professionalId}`,
      professionalId,
      plan: 'JobMatch Commission Marketplace',
      status: 'active',
      amount: 0,
      currency: 'NGN',
      startedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    status: 'active',
    isEligible: true,
    payments: [],
  };
}

export async function initializePaystackSubscription(
  professionalId: string,
  email: string,
  callbackUrl?: string
): Promise<{
  success: boolean;
  authorization_url?: string;
  reference?: string;
  access_code?: string;
  error?: string;
}> {
  try {
    const res = await fetch('/api/subscriptions/paystack/initialize', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ professionalId, email, callbackUrl }),
    });

    const data = await res.json();
    return data;
  } catch (err: any) {
    return {
      success: false,
      error: err.message || 'Network error while contacting Paystack initialization endpoint.',
    };
  }
}

export async function verifyPaystackSubscription(
  reference: string,
  professionalId?: string
): Promise<{
  success: boolean;
  message?: string;
  subscription?: ProfessionalSubscription;
  payment?: SubscriptionPayment;
  isEligible?: boolean;
  alreadyProcessed?: boolean;
  error?: string;
}> {
  try {
    const res = await fetch('/api/subscriptions/paystack/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reference, professionalId }),
    });

    const data = await res.json();
    return data;
  } catch (err: any) {
    return {
      success: false,
      error: err.message || 'Network error while verifying transaction with Paystack.',
    };
  }
}

export async function fetchPaystackClientConfig(): Promise<{
  publicKey: string;
  isConfigured: boolean;
  monthlyPriceNaira: number;
}> {
  try {
    const res = await fetch('/api/subscriptions/config');
    if (res.ok) {
      return await res.json();
    }
  } catch (err) {
    console.warn('Paystack config fetch notice:', err);
  }

  return {
    publicKey: '',
    isConfigured: false,
    monthlyPriceNaira: 5000,
  };
}

export async function fetchAdminSubscriptionsOverview(): Promise<{
  success: boolean;
  metrics: {
    totalArtisans: number;
    activeSubscriptions: number;
    expiredSubscriptions: number;
    successfulPaymentsCount: number;
    failedPaymentsCount: number;
    totalRevenueNaira: number;
  };
  subscriptions: any[];
  payments: any[];
}> {
  try {
    const res = await fetch('/api/admin/subscriptions-overview');
    if (res.ok) {
      return await res.json();
    }
  } catch (err) {
    console.warn('Admin subscription overview fetch notice:', err);
  }

  return {
    success: false,
    metrics: {
      totalArtisans: 0,
      activeSubscriptions: 0,
      expiredSubscriptions: 0,
      successfulPaymentsCount: 0,
      failedPaymentsCount: 0,
      totalRevenueNaira: 0,
    },
    subscriptions: [],
    payments: [],
  };
}

// ==============================================================================
// ADMIN CONTROL CENTRE API HELPERS
// ==============================================================================

export async function getAdminHeaders(extraHeaders: Record<string, string> = {}): Promise<Record<string, string>> {
  return await getAuthHeaders(extraHeaders);
}

/**
 * Universal Authenticated Request Headers Helper
 * Retrieves the authoritative Supabase Auth JWT token from the active session.
 * Automatically refreshes expiring or expired access tokens using Supabase Auth.
 */
export async function getAuthHeaders(extraHeaders: Record<string, string> = {}): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...extraHeaders,
  };

  try {
    let session = (await supabase.auth.getSession()).data.session;

    // Check if session is expired or within 60s of expiring
    const isExpiredOrClose = session?.expires_at && session.expires_at * 1000 < Date.now() + 60000;

    if (!session || isExpiredOrClose) {
      const { data: refreshData } = await supabase.auth.refreshSession();
      if (refreshData?.session) {
        session = refreshData.session;
      }
    }

    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
      if (session.user?.id) {
        headers['x-user-id'] = session.user.id;
      }
    }
  } catch (err) {
    console.warn('Failed to extract session token from Supabase client:', err);
  }

  // Fallback to localStorage sb-*-auth-token if getSession() did not resolve
  if (!headers['Authorization'] && typeof window !== 'undefined' && window.localStorage) {
    try {
      const tokenKey = Object.keys(window.localStorage).find((k) => k.startsWith('sb-') && k.endsWith('-auth-token'));
      if (tokenKey) {
        const rawToken = window.localStorage.getItem(tokenKey);
        if (rawToken) {
          const parsed = JSON.parse(rawToken);
          if (parsed.access_token) {
            headers['Authorization'] = `Bearer ${parsed.access_token}`;
          }
          if (parsed.user?.id && !headers['x-user-id']) {
            headers['x-user-id'] = parsed.user.id;
          }
        }
      }
    } catch (_) {}
  }

  return headers;
}

/**
 * Secure KYC API Fetch with automatic token refresh and single-retry authorization
 */
export async function kycApiFetch(endpoint: string, options: RequestInit = {}): Promise<any> {
  const customHeaders = (options.headers as Record<string, string>) || {};
  const initialHeaders = await getAuthHeaders(customHeaders);

  let res = await fetch(endpoint, {
    ...options,
    headers: initialHeaders,
  });

  // If unauthorized (401), attempt a token refresh and retry ONCE
  if (res.status === 401) {
    try {
      const { data: refreshData } = await supabase.auth.refreshSession();
      if (refreshData?.session?.access_token) {
        const refreshedHeaders = {
          ...initialHeaders,
          Authorization: `Bearer ${refreshData.session.access_token}`,
          ...(refreshData.session.user?.id ? { 'x-user-id': refreshData.session.user.id } : {}),
        };
        res = await fetch(endpoint, {
          ...options,
          headers: refreshedHeaders,
        });
      }
    } catch (refreshErr) {
      console.warn('KYC API fetch token refresh retry notice:', refreshErr);
    }
  }

  try {
    const data = await res.json();
    return data;
  } catch (parseErr) {
    if (!res.ok) {
      return { success: false, error: `Request failed with status ${res.status}` };
    }
    return { success: false, error: 'Failed to parse server response' };
  }
}

export async function fetchAdminMetrics() {
  try {
    const res = await fetch('/api/admin/metrics', {
      headers: await getAdminHeaders(),
    });
    if (res.ok) return await res.json();
  } catch (err) {
    console.warn('Admin metrics fetch warning:', err);
  }
  return { success: false, metrics: null };
}

export async function fetchAdminProfessionals() {
  try {
    const res = await fetch('/api/admin/professionals', {
      headers: await getAdminHeaders(),
    });
    if (res.ok) return await res.json();
  } catch (err) {
    console.warn('Admin professionals fetch warning:', err);
  }
  return { success: false, professionals: [] };
}

export async function performAdminProfessionalAction(
  professionalId: string,
  action: 'verify' | 'reject' | 'suspend' | 'reactivate' | 'request_resubmission' | 'resubmit',
  reason?: string,
  details?: Record<string, any>
) {
  try {
    const res = await fetch('/api/admin/professionals/action', {
      method: 'POST',
      headers: await getAdminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ professionalId, action, reason, details }),
    });
    const data = await res.json().catch(() => ({ success: false, error: `Server error (${res.status})` }));
    return data;
  } catch (err: any) {
    return { success: false, error: err?.message || 'Network error performing professional action' };
  }
}

export async function fetchAdminCustomers() {
  try {
    const res = await fetch('/api/admin/customers', {
      headers: await getAdminHeaders(),
    });
    if (res.ok) return await res.json();
  } catch (err) {
    console.warn('Admin customers fetch warning:', err);
  }
  return { success: false, customers: [] };
}

export async function performAdminCustomerAction(
  customerId: string,
  action: 'suspend' | 'reactivate',
  reason?: string
) {
  const res = await fetch('/api/admin/customers/action', {
    method: 'POST',
    headers: await getAdminHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ customerId, action, reason }),
  });
  return await res.json();
}

export async function fetchAdminJobs() {
  try {
    const res = await fetch('/api/admin/jobs', {
      headers: await getAdminHeaders(),
    });
    if (res.ok) return await res.json();
  } catch (err) {
    console.warn('Admin jobs fetch warning:', err);
  }
  return { success: false, jobs: [] };
}

export async function performAdminJobAction(
  jobId: string,
  action: 'flag' | 'unflag' | 'cancel' | 'update_status' | 'assign' | 'reassign' | 'unassign',
  reason?: string,
  adminNotes?: string,
  newStatus?: string,
  professionalId?: string
) {
  const res = await fetch('/api/admin/jobs/action', {
    method: 'POST',
    headers: await getAdminHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ jobId, action, reason, adminNotes, newStatus, professionalId }),
  });
  return await res.json();
}

export async function fetchAdminVerifications() {
  try {
    const res = await fetch('/api/admin/verifications', {
      headers: await getAdminHeaders(),
    });
    if (res.ok) return await res.json();
  } catch (err) {
    console.warn('Admin verifications fetch warning:', err);
  }
  return { success: false, verifications: [] };
}

export async function fetchAdminAIMonitoring() {
  try {
    const res = await fetch('/api/admin/ai-activity', {
      headers: await getAdminHeaders(),
    });
    if (res.ok) {
      const data = await res.json();
      return { success: Boolean(data.success), aiMetrics: data.aiMetrics || data.stats || null };
    }
    const errData = await res.json().catch(() => ({}));
    return { success: false, aiMetrics: null, error: errData.error || `Server responded with status ${res.status}` };
  } catch (err: any) {
    console.warn('Admin AI monitoring fetch warning:', err);
    return { success: false, aiMetrics: null, error: err.message || 'Network connectivity error' };
  }
}

export async function fetchAdminReviews() {
  try {
    const res = await fetch('/api/admin/reviews', {
      headers: await getAdminHeaders(),
    });
    if (res.ok) return await res.json();
  } catch (err) {
    console.warn('Admin reviews fetch warning:', err);
  }
  return { success: false, reviews: [] };
}

export async function performAdminReviewAction(
  reviewId: string,
  action: 'flag' | 'unflag' | 'hide' | 'restore',
  reason?: string
) {
  const res = await fetch('/api/admin/reviews/action', {
    method: 'POST',
    headers: await getAdminHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ reviewId, action, reason }),
  });
  return await res.json();
}

export async function fetchAdminDisputes() {
  try {
    const res = await fetch('/api/admin/disputes', {
      headers: await getAdminHeaders(),
    });
    if (res.ok) return await res.json();
  } catch (err) {
    console.warn('Admin disputes fetch warning:', err);
  }
  return { success: false, disputes: [] };
}

export async function updateAdminDispute(
  disputeId: string,
  status: 'OPEN' | 'UNDER_REVIEW' | 'RESOLVED' | 'CLOSED',
  resolutionNotes: string
) {
  const res = await fetch('/api/admin/disputes/update', {
    method: 'POST',
    headers: await getAdminHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ disputeId, status, resolutionNotes }),
  });
  return await res.json();
}

export async function fetchAdminAuditLogs() {
  try {
    const res = await fetch('/api/admin/audit-logs', {
      headers: await getAdminHeaders(),
    });
    if (res.ok) return await res.json();
  } catch (err) {
    console.warn('Admin audit logs fetch warning:', err);
  }
  return { success: false, auditLogs: [] };
}

export async function sendAdminBroadcastNotification(
  title: string,
  message: string,
  targetRole: 'all' | 'customer' | 'professional' = 'all'
) {
  const res = await fetch('/api/admin/notifications/broadcast', {
    method: 'POST',
    headers: await getAdminHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ title, message, targetRole }),
  });
  return await res.json();
}

export async function performAdminGlobalSearch(query: string) {
  try {
    const res = await fetch(`/api/admin/search?q=${encodeURIComponent(query)}`, {
      headers: await getAdminHeaders(),
    });
    if (res.ok) return await res.json();
  } catch (err) {
    console.warn('Admin search fetch warning:', err);
  }
  return {
    success: false,
    results: {
      customers: [],
      professionals: [],
      jobs: [],
      payments: [],
      whatsapp: [],
      disputes: [],
    },
  };
}

// Unified Aliases for Admin Control Centre Components
export async function fetchAdminUsers(role: 'professional' | 'customer') {
  if (role === 'professional') {
    const res = await fetchAdminProfessionals();
    return { success: res.success, users: res.professionals || [] };
  } else {
    const res = await fetchAdminCustomers();
    return { success: res.success, users: res.customers || [] };
  }
}

export async function fetchAdminVerificationSubmissions() {
  const res = await fetchAdminVerifications();
  return { success: res.success, submissions: res.verifications || [] };
}

export async function fetchAdminAIHealth(): Promise<{ success: boolean; stats: AdminAIHealthStats | null; error?: string }> {
  const res = await fetchAdminAIMonitoring();
  if (!res.success || !res.aiMetrics) {
    return { success: false, stats: null, error: res.error || 'Failed to fetch Gemini AI telemetry' };
  }

  const raw: any = res.aiMetrics;
  const total = raw.totalInferences ?? raw.totalJobsAnalyzed ?? (raw.recentLogs?.length || 0);
  const successCount = raw.successfulRequests ?? raw.aiSuccessCount ?? (raw.recentLogs?.filter((l: any) => l.success || l.status === 'success').length || 0);
  const fallbackCount = raw.failedRequests ?? raw.aiFallbackCount ?? Math.max(0, total - successCount);
  const successRate = raw.successRate ?? (total > 0 ? (successCount / total) * 100 : 100);
  const fallbackRate = raw.fallbackRate ?? (total > 0 ? (fallbackCount / total) * 100 : 0);
  const avgLatency = raw.avgLatencyMs ?? 420;

  const normalizedStats: AdminAIHealthStats = {
    totalInferences: total,
    totalJobsAnalyzed: total,
    successfulRequests: successCount,
    failedRequests: fallbackCount,
    successRate: Number(Number(successRate).toFixed(1)),
    fallbackRate: Number(Number(fallbackRate).toFixed(1)),
    avgLatencyMs: Math.round(avgLatency),
    totalTokensUsed: raw.totalTokensUsed ?? raw.totalTokens ?? null,
    inputTokensUsed: raw.inputTokensUsed ?? raw.inputTokens ?? null,
    outputTokensUsed: raw.outputTokensUsed ?? raw.outputTokens ?? null,
    estimatedCostUSD: raw.estimatedCostUSD ?? raw.estimatedCost ?? null,
    modelBreakdown: raw.modelBreakdown || {},
    categoryDistribution: raw.categoryDistribution || [],
    recentLogs: (raw.recentLogs || []).map((l: any) => ({
      id: l.id || `ai-log-${Math.random().toString(36).substring(2, 8)}`,
      timestamp: l.timestamp || new Date().toISOString(),
      source: l.source || l.feature || 'Job Analysis & Scope Engine',
      feature: l.feature || l.source || 'Job Understanding',
      prompt_preview: l.prompt_preview || l.jobTitle || 'Customer job request',
      jobTitle: l.jobTitle || l.prompt_preview || 'Customer job request',
      model_version: l.model_version || l.modelUsed || 'gemini-2.5-flash',
      modelUsed: l.modelUsed || l.model_version || 'gemini-2.5-flash',
      detected_category: l.detected_category || l.detectedCategory || 'General',
      detectedCategory: l.detectedCategory || l.detected_category || 'General',
      selected_category: l.selected_category || l.selectedCategory,
      selectedCategory: l.selectedCategory || l.selected_category,
      category_mismatch: Boolean(l.category_mismatch ?? l.categoryMismatch),
      categoryMismatch: Boolean(l.categoryMismatch ?? l.category_mismatch),
      mismatch_explanation: l.mismatch_explanation || l.mismatchExplanation || '',
      mismatchExplanation: l.mismatchExplanation || l.mismatch_explanation || '',
      safety_hazard: l.safety_hazard || l.safetyHazard || null,
      safetyHazard: l.safetyHazard || l.safety_hazard || null,
      estimated_complexity: l.estimated_complexity || l.estimatedComplexity || 'moderate',
      estimatedComplexity: l.estimatedComplexity || l.estimated_complexity || 'moderate',
      latency_ms: l.latency_ms ?? l.durationMs ?? 420,
      durationMs: l.durationMs ?? l.latency_ms ?? 420,
      success: l.success !== undefined ? Boolean(l.success) : l.status === 'success',
      status: l.status || (l.success ? 'success' : 'fallback'),
      tokens_input: l.tokens_input ?? l.tokensInput ?? null,
      tokens_output: l.tokens_output ?? l.tokensOutput ?? null,
      tokens_total: l.tokens_total ?? l.tokensTotal ?? null,
      cost_usd: l.cost_usd ?? l.costUsd ?? null,
      error_message: l.error_message || l.errorMessage || null,
    })),
  };

  return { success: true, stats: normalizedStats };
}

export async function performAdminReviewModeration(
  reviewId: string,
  action: 'flag' | 'unflag' | 'hide' | 'restore',
  reason?: string
) {
  return await performAdminReviewAction(reviewId, action, reason);
}

export async function performAdminDisputeResolution(
  disputeId: string,
  status: string,
  resolutionNotes: string
) {
  const normalizedStatus =
    status === 'investigating'
      ? 'UNDER_REVIEW'
      : status === 'resolved'
      ? 'RESOLVED'
      : status === 'dismissed'
      ? 'CLOSED'
      : 'OPEN';
  return await updateAdminDispute(disputeId, normalizedStatus as any, resolutionNotes);
}

export async function sendAdminBroadcast(payload: {
  title: string;
  message: string;
  targetAudience: 'all' | 'professionals' | 'customers';
  type: 'info' | 'warning' | 'alert';
}) {
  const targetRole =
    payload.targetAudience === 'professionals'
      ? 'professional'
      : payload.targetAudience === 'customers'
      ? 'customer'
      : 'all';
  return await sendAdminBroadcastNotification(payload.title, payload.message, targetRole);
}

// ==============================================================================
// PROFESSIONAL IDENTITY, KYC, AND TRUST & SAFETY CLIENT API HELPERS
// ==============================================================================

export async function fetchProfessionalKYCStatus(
  professionalId: string,
  requestingUserId?: string,
  requestingRole?: string
) {
  try {
    const extraHeaders: Record<string, string> = {};
    if (requestingUserId) extraHeaders['x-user-id'] = requestingUserId;
    if (requestingRole) extraHeaders['x-user-role'] = requestingRole;

    const data = await kycApiFetch(`/api/kyc/status/${professionalId}`, {
      headers: extraHeaders,
    });
    return data;
  } catch (err) {
    console.warn('KYC status fetch error:', err);
  }
  return { success: false, isPublicView: true, badges: null };
}

export async function uploadKYCDocument(data: {
  professionalId: string;
  documentType: 'government_id' | 'utility_bill' | 'selfie';
  fileName: string;
  fileDataBase64: string;
  fileType: string;
  fileSize: number;
}) {
  return await kycApiFetch('/api/kyc/upload-document', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function submitKYCConsent(
  professionalId: string,
  consentGiven: boolean,
  privacyPolicyVersion = 'NDPA-2023.v1',
  profileDetails?: {
    fullName?: string;
    phone?: string;
    email?: string;
    state?: string;
    lga?: string;
    address?: string;
    serviceCategory?: string;
  }
) {
  return await kycApiFetch('/api/kyc/consent', {
    method: 'POST',
    body: JSON.stringify({
      professionalId,
      consentGiven,
      privacyPolicyVersion,
      ...profileDetails,
    }),
  });
}

export async function submitKYCGovernmentId(data: {
  professionalId: string;
  idType: string;
  idNumber: string;
  idDocumentPath?: string;
  guarantorName?: string;
  guarantorPhone?: string;
  tradeCertifications?: string[];
}) {
  return await kycApiFetch('/api/kyc/identity', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function verifyInterswitchNINApi(data: {
  professionalId: string;
  nin: string;
  firstName?: string;
  lastName?: string;
  consentGiven: boolean;
}): Promise<{
  success: boolean;
  outcome?: string;
  status?: string;
  idNumberMasked?: string;
  providerReference?: string;
  verifiedAt?: string;
  error?: string;
  guidanceMessage?: string;
}> {
  return await kycApiFetch('/api/verification/interswitch/nin', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function fetchInterswitchStatusApi(professionalId: string): Promise<{
  success: boolean;
  status?: string;
  verificationProvider?: string | null;
  idNumberMasked?: string;
  providerReference?: string;
  verifiedAt?: string;
  verificationMethod?: string;
  error?: string;
}> {
  return await kycApiFetch(`/api/verification/interswitch/status?professionalId=${encodeURIComponent(professionalId)}`, {
    method: 'GET',
  });
}

export async function submitKYCAddress(data: {
  professionalId: string;
  addressDocumentType: string;
  declaredResidentialAddress: string;
  declaredState: string;
  declaredLga: string;
  addressDocumentPath?: string;
}) {
  return await kycApiFetch('/api/kyc/address', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function submitKYCLocation(data: {
  professionalId: string;
  latitude: number;
  longitude: number;
  accuracyMeters?: number;
}) {
  return await kycApiFetch('/api/kyc/location', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function submitKYCFacial(data: {
  professionalId: string;
  selfieDocumentPath?: string;
  livenessCheckPassed: boolean;
  selfieDataUrl?: string;
}) {
  return await kycApiFetch('/api/kyc/facial', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function submitKYCForReview(professionalId: string) {
  return await kycApiFetch('/api/kyc/submit-for-review', {
    method: 'POST',
    body: JSON.stringify({ professionalId }),
  });
}

export async function performAdminKYCDecision(data: {
  professionalId: string;
  decision: 'approve_all' | 'reject' | 'action_required' | 'suspend' | 'reopen' | 'expire';
  rejectionReason?: string;
  adminNotes?: string;
}) {
  const headers = await getAdminHeaders();
  const res = await fetch('/api/kyc/admin/decision', {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  return await res.json();
}

export async function submitSafetyReport(data: {
  professionalId: string;
  reportedById: string;
  jobId?: string;
  reasonCategory: string;
  description: string;
  evidenceUrls?: string[];
}) {
  const res = await fetch('/api/safety/report', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return await res.json();
}

export async function fetchAdminSafetyReports() {
  try {
    const headers = await getAdminHeaders();
    const res = await fetch('/api/safety/reports', {
      headers,
    });
    if (res.ok) return await res.json();
  } catch (err) {
    console.warn('Admin safety reports fetch error:', err);
  }
  return { success: false, reports: [] };
}

export async function performAdminSafetyReportAction(
  reportId: string,
  action: 'investigate' | 'take_action' | 'dismiss',
  adminNotes?: string,
  suspendProfessional = false
) {
  const headers = await getAdminHeaders();
  const res = await fetch(`/api/safety/reports/${reportId}/action`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ action, adminNotes, suspendProfessional }),
  });
  return await res.json();
}

export async function checkProfessionalKYCEligibility(professionalId: string) {
  try {
    const res = await fetch(`/api/kyc/eligibility/${professionalId}`);
    if (res.ok) return await res.json();
  } catch (err) {
    console.warn('KYC eligibility fetch error:', err);
  }
  return { success: false, isEligible: false, disqualificationReasons: ['Service check offline'] };
}

// =========================================================================
// PRODUCTION NOTIFICATION SYSTEM CLIENT HELPERS (EMAIL, SMS, WHATSAPP)
// =========================================================================

export async function fetchAdminNotificationDiagnostic() {
  try {
    const headers = await getAdminHeaders();
    const res = await fetch('/api/admin/notifications/diagnostic', {
      headers,
    });
    if (res.ok) return await res.json();
  } catch (err) {
    console.warn('Notification diagnostic fetch error:', err);
  }
  return {
    success: false,
    email: { configured: false, status: 'UNKNOWN' },
    sms: { configured: false, status: 'UNKNOWN' },
    database: { connected: false },
  };
}

export async function fetchAdminNotificationOverview() {
  try {
    const headers = await getAdminHeaders();
    const res = await fetch('/api/admin/notifications/overview', {
      headers,
    });
    if (res.ok) return await res.json();
  } catch (err) {
    console.warn('Notification overview fetch error:', err);
  }
  return {
    success: false,
    total: 0,
    emailSent: 0,
    emailFailed: 0,
    smsSent: 0,
    smsFailed: 0,
    whatsappSent: 0,
    whatsappFailed: 0,
  };
}

export async function fetchAdminNotifications(filters: {
  channel?: string;
  status?: string;
  search?: string;
  limit?: number;
} = {}) {
  try {
    const params = new URLSearchParams();
    if (filters.channel && filters.channel !== 'all') params.append('channel', filters.channel);
    if (filters.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.limit) params.append('limit', String(filters.limit));

    const headers = await getAdminHeaders();
    const res = await fetch(`/api/admin/notifications?${params.toString()}`, {
      headers,
    });
    if (res.ok) return await res.json();
  } catch (err) {
    console.warn('Admin notifications fetch error:', err);
  }
  return { success: false, records: [], count: 0 };
}

export async function retryAdminNotification(id: string) {
  const headers = await getAdminHeaders();
  const res = await fetch(`/api/admin/notifications/${id}/retry`, {
    method: 'POST',
    headers,
  });
  return await res.json();
}

export async function sendAdminTestEmail(email: string, name?: string) {
  const headers = await getAdminHeaders();
  const res = await fetch('/api/admin/notifications/test-email', {
    method: 'POST',
    headers,
    body: JSON.stringify({ email, name }),
  });
  return await res.json();
}

export async function sendAdminTestSms(phone: string, name?: string) {
  const headers = await getAdminHeaders();
  const res = await fetch('/api/admin/notifications/test-sms', {
    method: 'POST',
    headers,
    body: JSON.stringify({ phone, name }),
  });
  return await res.json();
}

export async function fetchProfessionalNotificationPreferences(proId: string) {
  try {
    const res = await fetch(`/api/professionals/${proId}/notification-preferences`);
    if (res.ok) return await res.json();
  } catch (err) {
    console.warn('Fetch pro notification preferences error:', err);
  }
  return {
    success: true,
    preferences: {
      professional_id: proId,
      email_enabled: true,
      sms_enabled: true,
      whatsapp_enabled: true,
    },
  };
}

export async function updateProfessionalNotificationPreferences(
  proId: string,
  prefs: { email_enabled?: boolean; sms_enabled?: boolean; whatsapp_enabled?: boolean }
) {
  const res = await fetch(`/api/professionals/${proId}/notification-preferences`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(prefs),
  });
  return await res.json();
}

export async function fetchCustomerNotificationPreferences(customerId: string) {
  try {
    const res = await fetch(`/api/customers/${customerId}/notification-preferences`);
    if (res.ok) return await res.json();
  } catch (err) {
    console.warn('Fetch customer notification preferences error:', err);
  }
  return {
    success: true,
    preferences: {
      customer_id: customerId,
      email_enabled: true,
      sms_enabled: true,
      whatsapp_enabled: true,
    },
  };
}

export async function updateCustomerNotificationPreferences(
  customerId: string,
  prefs: { email_enabled?: boolean; sms_enabled?: boolean; whatsapp_enabled?: boolean }
) {
  const res = await fetch(`/api/customers/${customerId}/notification-preferences`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(prefs),
  });
  return await res.json();
}

export async function triggerCustomerLifecycleNotification(params: {
  eventType: string;
  jobId?: string;
  job?: any;
  customer?: any;
  professional?: any;
  quote?: any;
  scheduleDate?: string;
  matchCount?: number;
}) {
  try {
    const res = await fetch('/api/notifications/customer-lifecycle-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export async function sendAdminTestCustomerEvent(params: {
  eventType: string;
  email?: string;
  phone?: string;
  name?: string;
}) {
  const headers = await getAdminHeaders();
  const res = await fetch('/api/admin/notifications/test-customer-event', {
    method: 'POST',
    headers,
    body: JSON.stringify(params),
  });
  return await res.json();
}

/**
 * Directly upgrade an existing customer account to an artisan / professional account
 * Records professional profile in Supabase database and updates user role
 */
export async function upgradeCustomerToProfessionalAccount(
  userId: string,
  payload: {
    fullName: string;
    businessName: string;
    email: string;
    phone: string;
    primaryCategory: string;
    secondaryCategories?: string[];
    serviceAreas?: string[];
    yearsExperience: number;
    hourlyRate: number;
    idNumber?: string;
    state: string;
    lga: string;
    address?: string;
    bio?: string;
  }
): Promise<Profile> {
  const res = await fetch('/api/auth/upgrade-to-professional', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userId,
      email: payload.email,
      fullName: payload.fullName,
      phone: payload.phone,
      state: payload.state,
      lga: payload.lga,
      address: payload.address || '',
      businessName: payload.businessName,
      primaryCategory: payload.primaryCategory,
      secondaryCategories: payload.secondaryCategories || [],
      serviceAreas: payload.serviceAreas || [payload.lga],
      yearsExperience: payload.yearsExperience,
      hourlyRate: payload.hourlyRate,
      idNumber: payload.idNumber,
      bio: payload.bio,
    }),
  });

  const data = await res.json();
  if (!data.success) {
    throw new Error(data.error || 'Failed to upgrade account to Professional.');
  }

  // Update client-side Supabase user metadata if signed in
  try {
    await supabase.auth.updateUser({
      data: {
        role: 'professional',
        full_name: payload.fullName,
        phone: payload.phone,
        primary_category: payload.primaryCategory,
      },
    });
  } catch (_) {}

  const proProfile: Profile = {
    id: userId,
    role: 'professional',
    fullName: payload.fullName,
    email: payload.email,
    phone: payload.phone,
    businessName: payload.businessName,
    primaryCategory: payload.primaryCategory,
    secondaryCategories: payload.secondaryCategories || [],
    yearsExperience: payload.yearsExperience,
    hourlyRate: payload.hourlyRate,
    verificationStatus: 'pending',
    idType: 'NIN (National Identity Management Commission)',
    idNumber: payload.idNumber,
    state: payload.state,
    lga: payload.lga,
    address: payload.address || '',
    bio: payload.bio || `Certified Nigerian artisan with ${payload.yearsExperience} years of experience in ${payload.primaryCategory}.`,
    avatarUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&auto=format&fit=crop&q=80',
    rating: 5.0,
    reviewCount: 0,
    completedJobsCount: 0,
    isAvailable: true,
    serviceAreas: payload.serviceAreas && payload.serviceAreas.length > 0 ? payload.serviceAreas : [payload.lga],
    tradeCertifications: ['Federal Trade Test Grade 1 & 2', 'NIMC National ID'],
    createdAt: new Date().toISOString(),
  };

  return proProfile;
}

/**
 * Authoritatively fetch a single public professional's profile
 * Returns null if the profile does not exist or is not a professional.
 * Never substitutes another user or fake profile.
 */
export async function fetchPublicProfessionalProfile(proId: string): Promise<Profile | null> {
  if (!proId || typeof proId !== 'string' || !proId.trim()) return null;
  const cleanId = proId.trim();

  // 1. Try server endpoint first
  try {
    const res = await fetch(`/api/professionals/${encodeURIComponent(cleanId)}`);
    if (res.ok) {
      const data = await res.json();
      if (data && data.success && data.professional) {
        return data.professional as Profile;
      }
    }
  } catch (apiErr) {
    console.warn('[fetchPublicProfessionalProfile] Server endpoint notice:', apiErr);
  }

  // 2. Direct Supabase query fallback
  try {
    const { data: dbProfile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', cleanId)
      .maybeSingle();

    if (dbProfile && (dbProfile.role === 'professional' || !dbProfile.role)) {
      const { data: proDetails } = await supabase
        .from('professional_profiles')
        .select('*')
        .eq('id', cleanId)
        .maybeSingle();

      const { data: verifDetails } = await supabase
        .from('professional_verifications')
        .select('verification_status, id_type, trade_certifications')
        .eq('professional_id', cleanId)
        .maybeSingle();

      const { data: serviceAreas } = await supabase
        .from('service_areas')
        .select('lga')
        .eq('professional_id', cleanId);

      const pro: Profile = {
        id: dbProfile.id,
        role: 'professional',
        fullName: dbProfile.full_name || 'Verified Professional',
        email: dbProfile.email || '',
        phone: cleanContactPhone(dbProfile.phone),
        avatarUrl: dbProfile.avatar_url || '',
        isProfilePhotoVerified: Boolean(dbProfile.is_profile_photo_verified),
        state: dbProfile.state || 'Lagos',
        lga: dbProfile.lga || 'Ikeja',
        cityArea: dbProfile.city_area,
        address: '',
        businessName:
          proDetails?.business_name ||
          (dbProfile.full_name ? `${dbProfile.full_name} Technical Services` : 'Verified Technical Services'),
        primaryCategory: proDetails?.primary_category || 'plumber',
        secondaryCategories: proDetails?.secondary_categories || [],
        specialties: proDetails?.specialties || [],
        skills: proDetails?.skills || [],
        yearsExperience: proDetails?.years_experience || 3,
        hourlyRate: proDetails?.hourly_rate || 8000,
        bio: proDetails?.bio || '',
        isAvailable: proDetails?.is_available ?? true,
        verificationStatus: verifDetails?.verification_status || dbProfile.verification_status || 'verified',
        tradeCertifications: verifDetails?.trade_certifications || [],
        serviceAreas:
          serviceAreas && serviceAreas.length > 0 ? serviceAreas.map((a: any) => a.lga) : [dbProfile.lga || 'Ikeja'],
        rating: proDetails?.rating || 5.0,
        reviewCount: proDetails?.review_count || 0,
        completedJobsCount: proDetails?.completed_jobs_count || 0,
        createdAt: dbProfile.created_at || new Date().toISOString(),
      };
      return pro;
    }
  } catch (err) {
    console.warn('[fetchPublicProfessionalProfile] Supabase query notice:', err);
  }

  return null;
}

// ============================================================================
// Job Cancellation, Professional No-Show Reporting, Reassignment & Preferences
// ============================================================================

async function getBearerAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const { data } = await supabase.auth.getSession();
    if (data?.session?.access_token) {
      headers['Authorization'] = `Bearer ${data.session.access_token}`;
    }
  } catch {
    // Ignore
  }
  return headers;
}

export async function cancelJobApi(
  jobId: string,
  payload: {
    customerId?: string;
    reason: string;
    workStarted?: boolean;
    cancellationType?: 'before_hire' | 'before_work_started' | 'no_show' | 'work_started_dispute';
  }
): Promise<{
  success: boolean;
  job?: Job;
  outcome?: 'cancelled_open' | 'cancelled_refunded' | 'routed_to_dispute';
  refundProcessed?: boolean;
  disputeId?: string | null;
  message?: string;
  error?: string;
}> {
  try {
    const headers = await getBearerAuthHeaders();
    const res = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/cancel`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err?.message || 'Failed to cancel job.' };
  }
}

export async function reportNoShowApi(
  jobId: string,
  payload: {
    customerId?: string;
    reason: string;
  }
): Promise<{
  success: boolean;
  job?: Job;
  noShowProfessionalId?: string;
  message?: string;
  error?: string;
}> {
  try {
    const headers = await getBearerAuthHeaders();
    const res = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/report-no-show`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err?.message || 'Failed to report professional no-show.' };
  }
}

export interface ReassignmentCandidate {
  id: string;
  name?: string;
  fullName: string;
  email?: string;
  phone?: string;
  state: string;
  lga: string;
  rating: number;
  jobsCompleted: number;
  existingQuoteAmount?: number;
}

export interface CustomerNotificationPreferences {
  customer_id?: string;
  email_enabled?: boolean;
  sms_enabled?: boolean;
  in_app_enabled?: boolean;
  push_enabled?: boolean;
  whatsapp_enabled?: boolean;
  quote_alerts?: boolean;
  quote_received?: boolean;
  job_accepted?: boolean;
  job_completed?: boolean;
  payment_escrow_updates?: boolean;
  security_alerts?: boolean;
  login_alerts?: boolean;
  match_alerts?: boolean;
  booking_updates?: boolean;
  job_status_alerts?: boolean;
  marketing_emails?: boolean;
  sms_critical_only?: boolean;
  [key: string]: any;
}

export async function fetchReassignmentCandidatesApi(jobId: string): Promise<{
  success: boolean;
  candidates: ReassignmentCandidate[];
  error?: string;
}> {
  try {
    const res = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/reassignment-candidates`);
    const data = await res.json();
    return {
      success: Boolean(data.success),
      candidates: Array.isArray(data.candidates) ? data.candidates : [],
      error: data.error,
    };
  } catch (err: any) {
    return { success: false, candidates: [], error: err?.message || 'Failed to load replacement professionals.' };
  }
}

export async function reassignJobApi(
  jobId: string,
  payload: {
    customerId?: string;
    actorId?: string;
    actorRole?: string;
    newProfessionalId: string;
    reason?: string;
  }
): Promise<{
  success: boolean;
  job?: Job;
  newProfessionalId?: string;
  newProfessionalName?: string;
  escrowTransferred?: boolean;
  message?: string;
  error?: string;
}> {
  try {
    const headers = await getBearerAuthHeaders();
    const res = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/reassign`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err?.message || 'Failed to reassign job.' };
  }
}

export async function fetchCustomerNotificationPreferencesApi(customerId: string): Promise<{
  success: boolean;
  preferences?: any;
  error?: string;
}> {
  try {
    const res = await fetch(`/api/customers/${encodeURIComponent(customerId)}/notification-preferences`);
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err?.message || 'Failed to fetch notification preferences.' };
  }
}

export async function updateCustomerNotificationPreferencesApi(
  customerId: string,
  updates: Record<string, any>
): Promise<{
  success: boolean;
  preferences?: any;
  error?: string;
}> {
  try {
    const headers = await getBearerAuthHeaders();
    const res = await fetch(`/api/customers/${encodeURIComponent(customerId)}/notification-preferences`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updates),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err?.message || 'Failed to update notification preferences.' };
  }
}

export async function notifyLoginActivityApi(payloadOrUserId: string | {
  userId: string;
  email?: string;
  fullName?: string;
}): Promise<void> {
  try {
    const payload = typeof payloadOrUserId === 'string' ? { userId: payloadOrUserId } : payloadOrUserId;
    const headers = await getBearerAuthHeaders();
    await fetch('/api/auth/notify-login', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
  } catch {
    // Non-blocking
  }
}

export async function notifyProfileUpdatedApi(
  payloadOrUserId: string | {
    userId: string;
    email?: string;
    fullName?: string;
    updatedFields: string[];
  },
  maybeFields?: string[]
): Promise<void> {
  try {
    const payload =
      typeof payloadOrUserId === 'string'
        ? { userId: payloadOrUserId, updatedFields: maybeFields || [] }
        : payloadOrUserId;
    const headers = await getBearerAuthHeaders();
    await fetch('/api/auth/notify-profile-updated', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
  } catch {
    // Non-blocking
  }
}

export async function markJobArrivedApi(
  jobId: string,
  payload?: { professionalId?: string; arrivalNotes?: string }
): Promise<{
  success: boolean;
  job?: any;
  message?: string;
  error?: string;
}> {
  try {
    const headers = await getBearerAuthHeaders();
    const res = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/mark-arrived`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload || {}),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err?.message || 'Failed to record arrival.' };
  }
}

export async function reportJobDelayApi(
  jobId: string,
  payload: { professionalId?: string; delayMinutes: number; reason: string }
): Promise<{
  success: boolean;
  job?: any;
  newEstimatedArrival?: string;
  message?: string;
  error?: string;
}> {
  try {
    const headers = await getBearerAuthHeaders();
    const res = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/report-delay`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err?.message || 'Failed to report delay.' };
  }
}

export async function rescheduleJobAppointmentApi(
  jobId: string,
  payload: { actorId?: string; actorRole?: 'customer' | 'professional' | 'admin'; newScheduledTime: string; reason: string }
): Promise<{
  success: boolean;
  job?: any;
  message?: string;
  error?: string;
}> {
  try {
    const headers = await getBearerAuthHeaders();
    const res = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/reschedule`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err?.message || 'Failed to reschedule appointment.' };
  }
}

export async function evaluateJobNoShowApi(
  jobId: string,
  force?: boolean
): Promise<{
  success: boolean;
  result?: any;
  error?: string;
}> {
  try {
    const headers = await getBearerAuthHeaders();
    const res = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/evaluate-no-show`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ force: Boolean(force) }),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err?.message || 'Failed to evaluate no-show.' };
  }
}

export async function proposeJobArrivalEtaApi(
  jobId: string,
  payload: {
    professionalId?: string;
    etaMinutes?: number;
    proposedArrivalTime?: string;
    notes?: string;
  }
): Promise<{
  success: boolean;
  job?: any;
  proposedEtaAt?: string;
  proposedEtaMinutes?: number;
  etaAgreementDeadlineAt?: string;
  message?: string;
  error?: string;
}> {
  try {
    const headers = await getBearerAuthHeaders();
    const res = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/propose-eta`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err?.message || 'Failed to propose arrival ETA.' };
  }
}

export async function confirmJobArrivalEtaApi(
  jobId: string,
  payload?: { customerId?: string }
): Promise<{
  success: boolean;
  job?: any;
  agreedEtaAt?: string;
  gracePeriodMinutes?: number;
  message?: string;
  error?: string;
}> {
  try {
    const headers = await getBearerAuthHeaders();
    const res = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/confirm-eta`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload || {}),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err?.message || 'Failed to confirm arrival ETA.' };
  }
}

export async function confirmProfessionalArrivalByCustomerApi(
  jobId: string,
  payload?: { customerId?: string; notes?: string }
): Promise<{
  success: boolean;
  job?: any;
  arrivedAt?: string;
  message?: string;
  error?: string;
}> {
  try {
    const headers = await getBearerAuthHeaders();
    const res = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/confirm-arrival`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload || {}),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err?.message || 'Failed to confirm professional arrival.' };
  }
}

export async function sendTwoFactorCodeApi(payload: {
  email?: string;
  phone?: string;
  password?: string;
  challengeToken?: string;
  fullName?: string;
  userType?: 'customer' | 'professional';
  purpose?: 'registration' | 'login_2fa' | 'email_verification';
}): Promise<{
  success: boolean;
  requiresTwoFactor?: boolean;
  trustedDevice?: boolean;
  twoFactorVerified?: boolean;
  twoFactorSessionToken?: string;
  deviceToken?: string;
  session?: {
    access_token: string;
    refresh_token: string;
    expires_in?: number;
    expires_at?: number;
    token_type?: string;
  };
  user?: any;
  role?: 'customer' | 'professional' | 'admin';
  challengeToken?: string;
  message?: string;
  maskedPhone?: string;
  maskedEmail?: string;
  expiresInSeconds?: number;
  cooldownSeconds?: number;
  requiresVerificationCompletion?: boolean;
  sessionId?: string;
  activeStage?: 'email' | 'phone';
  emailVerified?: boolean;
  phoneVerified?: boolean;
  email?: string;
  phone?: string;
  fullName?: string;
  error?: string;
}> {
  try {
    const res = await fetch('/api/auth/2fa/send', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err?.message || 'Failed to send verification code.' };
  }
}

export async function verifyTwoFactorCodeApi(payload: {
  email?: string;
  phone?: string;
  otp: string;
  userId?: string;
  challengeToken?: string;
  purpose?: 'registration' | 'login_2fa';
}): Promise<{
  success: boolean;
  twoFactorVerified?: boolean;
  trustedDevice?: boolean;
  deviceToken?: string;
  deviceTrustedUntil?: string;
  twoFactorSessionToken?: string;
  session?: {
    access_token: string;
    refresh_token: string;
    expires_in?: number;
    expires_at?: number;
    token_type?: string;
  };
  user?: any;
  profile?: any;
  role?: 'customer' | 'professional' | 'admin';
  message?: string;
  remainingAttempts?: number;
  error?: string;
}> {
  try {
    const res = await fetch('/api/auth/2fa/verify', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err?.message || 'Failed to verify code.' };
  }
}
